import "../styles/adminWorkspace.css";
// src/pages/OwnerConsolePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import OwnerBusinessMenu from '../components/OwnerBusinessMenu';
import { isBusinessTrashed, businessTrashPatch, businessRestorePatch } from '../utils/businessTrash';
import {
  Building2,
  Check,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Search,
  Star,
  StarOff,
  X,
} from "lucide-react";
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  limit,
} from "firebase/firestore";
import { db as defaultDb, auth as defaultAuth } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./OwnerConsoleCompact.css";
import { ownerRangeBounds, ownerCurrentMonth, shiftOwnerMonth } from "../utils/ownerAnalyticsRange";
import { buildOwnerTrend } from "../utils/ownerAnalyticsTrend";
import { OwnerPaymentGovernance } from "../components/finance/LoopPaymentGovernance";
import LoopSettlements from "../components/finance/LoopSettlements";
import OwnerFinanceSettingsPanel from "../components/admin/OwnerFinanceSettingsPanel";
import OwnerPlatformFinancialSummaryPanel from "../components/admin/OwnerPlatformFinancialSummaryPanel";
import {
  callLoopOwnerBusinessAdmin,
  getLoopEffectiveAuthIdentity,
  isLoopPlatformOwnerIdentity,
  LOOP_PLATFORM_OWNER_EMAIL,
} from "../utils/platformOwner";

const OWNER_EMAIL = LOOP_PLATFORM_OWNER_EMAIL;
const OWNER_CONSOLE_BUSINESS_CACHE_KEY = "loop_owner_console_businesses_v5_server_fast";
const OWNER_CONSOLE_PLATFORM_CACHE_KEY = "loop_owner_console_platform_v5_server_fast";
const OWNER_CONSOLE_BUSINESS_CACHE_TTL_MS = 1000 * 60 * 10;
const OWNER_CONSOLE_PLATFORM_CACHE_TTL_MS = 1000 * 60 * 5;
const OWNER_CONSOLE_SERVER_TIMEOUT_MS = 12000;
const OWNER_CONSOLE_CLIENT_LIMITS = {
  businesses: 1500,
  bookings: 1800,
  businessFallbackBookings: 250,
  businessFallbackBusinesses: 120,
  analyticsEvents: 700,
};
const BRAND = "#00675f";
const BRAND_2 = "#00897f";

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function toMs(value) {
  try {
    if (!value) return 0;
    if (typeof value.toDate === "function") return value.toDate().getTime();
    if (typeof value === "number") return value > 100000000000 ? value : value * 1000;
    if (value.seconds) return Number(value.seconds) * 1000;
    if (value._seconds) return Number(value._seconds) * 1000;
    if (value.__seconds) return Number(value.__seconds) * 1000;
    if (value.iso) {
      const dIso = new Date(value.iso);
      return Number.isNaN(dIso.getTime()) ? 0 : dIso.getTime();
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  } catch (_) {
    return 0;
  }
}

function formatDate(value) {
  const ms = toMs(value);
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString("es-PA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return "—";
  }
}

function startOfLocalDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfLocalDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function ownerIsSameLocalDay(ms, date = new Date()) {
  const n = Number(ms || 0);
  if (!Number.isFinite(n) || n <= 0) return false;
  return n >= startOfLocalDay(date).getTime() && n <= endOfLocalDay(date).getTime();
}

function monthKeyFromMs(ms) {
  if (!ms) return "Sin fecha";
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function dayKeyFromMs(ms) {
  if (!ms) return "Sin fecha";
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getBusinessName(item) {
  return item?.businessName || item?.name || item?.displayName || item?.title || "Sin nombre";
}

function getBusinessOwner(item) {
  return item?.ownerEmail || item?.email || item?.createdByEmail || item?.userEmail || item?.owner?.email || "—";
}

function getBusinessCategory(item) {
  return item?.businessType || item?.category || item?.type || item?.serviceType || item?.industry || "Sin categoría";
}

function getBusinessImage(item) {
  return String(
    item?.logoUrl ||
    item?.logoURL ||
    item?.logo ||
    item?.imageUrl ||
    item?.mainPhotoUrl ||
    item?.coverImageUrl ||
    item?.coverImage ||
    ""
  ).trim();
}

function getBusinessSlug(item) {
  return String(item?.slug || item?.publicSlug || item?.businessSlug || item?.urlSlug || "").trim();
}

function getBusinessPublicPath(item) {
  const slug = getBusinessSlug(item);
  const id = String(item?.id || "").trim();
  const route = slug || id;
  return route ? `/business/${encodeURIComponent(route)}` : "";
}

function getBusinessEditPath(item) {
  const id = String(item?.id || "").trim();
  return id ? `/business/${encodeURIComponent(id)}/edit` : "";
}

function businessSearchHaystack(item) {
  return [
    item?.id,
    getBusinessName(item),
    getBusinessOwner(item),
    getBusinessCategory(item),
    getBusinessSlug(item),
    item?.phone,
    item?.address,
    item?.district,
    item?.province,
  ].filter(Boolean).join(" ").toLowerCase();
}

function isBusinessHidden(item) {
  return item?.hidden === true || item?.visible === false || item?.isActive === false;
}

function isBusinessFeatured(item) {
  return item?.featured === true || item?.isFeatured === true || item?.recommended === true;
}

function approvalStatus(item) {
  if (isBusinessTrashed(item)) return "deleted";
  const raw = String(item?.approvalStatus || item?.status || "").trim().toLowerCase();
  if (["draft","changes_requested"].includes(raw)) return raw;
  if (["approved", "active", "aprobado", "aprobada"].includes(raw)) return "approved";
  if (["rejected", "rechazado", "rechazada"].includes(raw)) return "rejected";
  if (item?.approved === true || item?.isApproved === true) return "approved";
  if (item?.visible === false || item?.hidden === true || item?.isActive === false) return raw === "rejected" ? "rejected" : "pending";
  return raw || "pending";
}

function statusLabel(value) {
  const s = String(value || "pending").toLowerCase();
  if (s === "deleted") return "Eliminada";
  if (s === "approved") return "Aprobada";
  if (s === "rejected") return "Rechazada";
  if (s === "pending") return "Pendiente";
  if (s === "draft") return "En preparación";
  if (s === "changes_requested") return "Requiere cambios";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function statusClass(value) {
  const s = String(value || "pending").toLowerCase();
  if (s === "approved") return "is-approved";
  if (s === "rejected") return "is-rejected";
  return "is-pending";
}

function getBookingDateMs(item) {
  // Booking docs have evolved over phases. Read every known date field so
  // historical/manual bookings do not disappear from analytics.
  return (
    toMs(item?.scheduledAt) ||
    toMs(item?.startAt) ||
    toMs(item?.start) ||
    toMs(item?.dateTime) ||
    toMs(item?.bookingDate) ||
    toMs(item?.appointmentDate) ||
    toMs(item?.requestedAt) ||
    toMs(item?.createdAt) ||
    toMs(item?.endAt) ||
    toMs(item?.end) ||
    toMs(item?.date) ||
    0
  );
}

function getBookingStatus(item) {
  const raw = String(item?.status || item?.confirmationStatus || item?.bookingStatus || "").trim().toLowerCase();
  if (["confirmed", "confirmada", "approved", "aprobada", "accepted", "completed", "done"].includes(raw)) return "confirmed";
  if (["pending", "pendiente", "to_confirm", "por_confirmar", "new"].includes(raw)) return "pending";
  if (["cancelled", "canceled", "cancelada", "rejected", "declined", "rechazada", "no_show"].includes(raw)) return "cancelled";
  return raw || "unknown";
}

function getBookingSource(item) {
  const rawFields = [
    item?.bookingSource,
    item?.createdFrom,
    item?.origin,
    item?.channel,
    item?.createdByRole,
    item?.createdVia,
  ].map((x) => String(x || "").trim().toLowerCase()).filter(Boolean);

  const raw = rawFields.join(" ");

  // IMPORTANT:
  // customerId/customerName/customerPhone/customerEmail only means the booking has
  // a customer attached. It does NOT mean the booking was created online.
  const hasStrongOnlineSignals = Boolean(
    item?.bookingSource === "client_online" ||
    item?.bookingSource === "online" ||
    item?.onlineBooking === true ||
    item?.isOnlineBooking === true ||
    item?.createdByClient === true ||
    item?.requestedByClient === true ||
    raw.includes("client_online") ||
    raw.includes("booking_interface") ||
    raw.includes("business_page_booking") ||
    raw.includes("public_booking_flow") ||
    raw.includes("web_booking") ||
    raw.includes("createbookingfunction")
  );

  const hasStrongManualSignals = Boolean(
    item?.bookingSource === "admin_manual" ||
    item?.bookingSource === "worker_manual" ||
    item?.manual === true ||
    item?.isManual === true ||
    item?.adminCreated === true ||
    item?.workerCreated === true ||
    item?.createdByOwner === true ||
    item?.createdByAdmin === true ||
    item?.createdByWorker === true ||
    raw.includes("admin_manual") ||
    raw.includes("worker_manual") ||
    raw.includes("admin_calendar") ||
    raw.includes("booking_create_modal") ||
    raw.includes("manual") ||
    raw.includes("backoffice") ||
    raw.includes("owner") ||
    raw.includes("worker")
  );

  if (hasStrongOnlineSignals) return "online";
  if (hasStrongManualSignals) return "manual";

  const path = String(item?.path || "").toLowerCase();
  const isBusinessBookingPath = path.includes("/bookings/") || path.startsWith("businesses/");
  const hasCustomerAttached = Boolean(
    item?.customerId ||
    item?.clientId ||
    item?.customerName ||
    item?.clientName ||
    item?.customerPhone ||
    item?.customerEmail ||
    item?.customerSnapshot
  );

  // Historical admin bookings often have a customer attached but no source fields.
  // Treat these as probable manual instead of online.
  if (isBusinessBookingPath && hasCustomerAttached) return "manual_probable";

  return "unknown";
}

function bookingSourceLabel(source) {
  const s = String(source || "unknown").toLowerCase();
  if (s === "online") return "Online confirmado";
  if (s === "manual") return "Manual confirmado";
  if (s === "manual_probable") return "Manual probable";
  return "Sin origen";
}

function bookingSourceClass(source) {
  const s = String(source || "unknown").toLowerCase();
  if (s === "online") return "is-online";
  if (s === "manual") return "is-manual";
  if (s === "manual_probable") return "is-manual-probable";
  return "is-unknown";
}

function getBookingBusinessId(item) {
  return String(
    item?.businessId ||
    item?.bizId ||
    item?.companyId ||
    item?.business?.id ||
    item?.businessSnapshot?.id ||
    "sin negocio"
  );
}

function ownerBusinessIdSet(businesses = []) {
  return new Set((Array.isArray(businesses) ? businesses : [])
    .map((biz) => String(biz?.id || "").trim())
    .filter(Boolean));
}

function ownerBookingLooksLikeRealBooking(item = {}) {
  return Boolean(
    item?.serviceId ||
    item?.serviceName ||
    item?.service ||
    item?.packageName ||
    item?.customerName ||
    item?.clientName ||
    item?.customerId ||
    item?.clientId ||
    item?.scheduledAt ||
    item?.startAt ||
    item?.start ||
    item?.date ||
    item?.createdAt
  );
}

function ownerFilterTrustedBookings(rows = [], businesses = [], options = {}) {
  const businessIds = ownerBusinessIdSet(businesses);
  const allowUnknownBusiness = options.allowUnknownBusiness === true;
  const maxFutureMs = Date.now() + 1000 * 60 * 60 * 24 * 370; // allow future appointments, but not broken timestamps
  const minMs = new Date(2024, 0, 1).getTime();
  return (Array.isArray(rows) ? rows : []).filter((booking) => {
    if (!ownerBookingLooksLikeRealBooking(booking)) return false;
    const ms = getBookingDateMs(booking);
    if (!ms || ms < minMs || ms > maxFutureMs) return false;
    const bid = getBookingBusinessId(booking);
    if (!bid || bid === "sin negocio") return allowUnknownBusiness;
    if (!businessIds.size) return true;
    return businessIds.has(String(bid));
  });
}

function ownerMetricRangeStart() {
  return getDateRangeStart("30d");
}

function getBookingBusinessName(item, businesses = []) {
  const direct = item?.businessName || item?.companyName || item?.business?.name || item?.businessSnapshot?.name;
  if (direct) return String(direct);
  const businessId = getBookingBusinessId(item);
  const found = businesses.find((biz) => String(biz.id) === String(businessId));
  return found ? getBusinessName(found) : businessId;
}

function getBookingServiceName(item) {
  return item?.serviceName || item?.service || item?.packageName || item?.serviceTitle || "Sin servicio";
}

function getBookingClientName(item) {
  return item?.customerName || item?.clientName || item?.name || item?.customer?.name || "Cliente";
}

function getBookingRevenueValue(item) {
  const raw = item?.totalPrice ?? item?.total ?? item?.price ?? item?.servicePrice ?? item?.amount ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function formatMoney(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "$0";
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

function getDateRangeStart(rangeKey) { return ownerRangeBounds(rangeKey).start; }

function getDateRangeLabel(rangeKey) {
  if (/^\d{4}-\d{2}$/.test(rangeKey)) return new Date(`${rangeKey}-01T00:00:00`).toLocaleDateString("es-PA", { month: "long", year: "numeric" });
  if (rangeKey === "7d") return "Últimos 7 días";
  if (rangeKey === "30d") return "Últimos 30 días";
  if (rangeKey === "90d") return "Últimos 90 días";
  if (rangeKey === "month") return "Mes actual";
  return "Todo";
}

function getAnalyticsEventTimeMs(item) {
  return toMs(item?.occurredAt) || toMs(item?.createdAt) || toMs(item?.timestamp) || 0;
}

function getAnalyticsEventType(item) {
  return String(item?.type || item?.eventType || item?.name || item?.action || "").trim();
}

function getAnalyticsBusinessId(item) {
  return String(item?.businessId || item?.bizId || item?.business?.id || "").trim();
}

function ownerConsoleErrorMessage(err) {
  try {
    return String(err?.code || err?.message || err || "unknown-error").slice(0, 260);
  } catch (_) {
    return "unknown-error";
  }
}

function ownerBookingDedupeKey(item) {
  const path = String(item?.path || "").trim();
  if (path) return path;
  const id = String(item?.id || item?.bookingId || "").trim();
  const businessId = getBookingBusinessId(item);
  return `${businessId || "unknown"}:${id || JSON.stringify([
    item?.customerName,
    item?.date,
    item?.time,
    item?.scheduledAt?.seconds || item?.scheduledAt,
  ])}`;
}

function ownerBookingHasValidDate(item) {
  return getBookingDateMs(item) > 0;
}

function ownerBookingHasSource(item) {
  return Boolean(
    item?.bookingSource ||
    item?.createdFrom ||
    item?.createdByRole ||
    item?.createdVia ||
    item?.origin ||
    item?.channel ||
    item?.onlineBooking === true ||
    item?.isOnlineBooking === true ||
    item?.manual === true ||
    item?.isManual === true ||
    item?.adminCreated === true ||
    item?.createdByClient === true ||
    item?.requestedByClient === true
  );
}

function ownerPathRoot(path = "") {
  const parts = String(path || "").split("/").filter(Boolean);
  if (!parts.length) return "sin ruta";
  if (parts[0] === "businesses" && parts[2] === "bookings") return "business subcollection";
  return parts[0];
}

function ownerNormalizeServerRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({ ...(row || {}) }));
}

function buildOwnerDataHealth({ businesses = [], bookings = [], analyticsEvents = [], serverHealth = null, errors = [] }) {
  const health = {
    loadedAt: new Date().toISOString(),
    source: serverHealth ? "cloud_function_ownerListPlatformData" : "client_reader",
    businessesLoaded: businesses.length,
    bookingsLoaded: bookings.length,
    collectionGroupBookings: Number(serverHealth?.collectionGroupBookings || 0),
    businessFallbackBookings: Number(serverHealth?.businessFallbackBookings || 0),
    rootFallbackBookings: Number(serverHealth?.rootFallbackBookings || 0),
    analyticsEventsLoaded: analyticsEvents.length,
    trustedFilter: true,
    platformEventsLoaded: Number(serverHealth?.platformEventsLoaded || 0),
    businessAnalyticsEventsLoaded: Number(serverHealth?.businessAnalyticsEventsLoaded || 0),
    invalidDateBookings: 0,
    missingSourceBookings: 0,
    pathRoots: {},
    methodsUsed: Array.isArray(serverHealth?.methodsUsed) ? serverHealth.methodsUsed : [],
    errors: Array.isArray(errors) ? errors : [],
  };

  bookings.forEach((booking) => {
    if (!ownerBookingHasValidDate(booking)) health.invalidDateBookings += 1;
    if (!ownerBookingHasSource(booking)) health.missingSourceBookings += 1;
    const root = ownerPathRoot(booking.path);
    health.pathRoots[root] = (health.pathRoots[root] || 0) + 1;
  });

  return health;
}

function OwnerMetricCard({ label, value, note, tone = "default" }) {
  return (
    <article className={`owner-metric-card ${tone ? `tone-${tone}` : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {note ? <small>{note}</small> : null}
    </article>
  );
}

function OwnerEmptyPanel({ title, children }) {
  return (
    <div className="owner-empty-panel">
      <div className="owner-empty-icon">∞</div>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

function formatSignedPercent(value) {
  if (value === null || value === undefined) return "Sin comparación";
  const n = Number(value);
  if (!Number.isFinite(n)) return "Sin comparación";
  if (n === 0) return "0% vs periodo anterior";
  return `${n > 0 ? "+" : ""}${n}% vs periodo anterior`;
}

function percentDelta(current, previous) {
  const c = Number(current || 0);
  const p = Number(previous || 0);
  if (!Number.isFinite(c) || !Number.isFinite(p) || p <= 0) return null;
  return Math.round(((c - p) / p) * 100);
}

function compactPercent(part, total) {
  const t = Number(total || 0);
  if (!t) return 0;
  return Math.round((Number(part || 0) / t) * 100);
}

function sourceGroupLabel(source) {
  const s = String(source || "unknown").toLowerCase();
  if (s === "online") return "Online";
  if (s === "manual" || s === "manual_probable") return "Manual";
  return "Por clasificar";
}


function readOwnerConsoleCache(key, ttlMs = 0) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (ttlMs > 0 && parsed.savedAt && Date.now() - Number(parsed.savedAt) > ttlMs) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeOwnerConsoleCache(key, payload) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(key, JSON.stringify({ ...(payload || {}), savedAt: Date.now() }));
  } catch (_) {}
}

function clearOwnerConsoleCaches() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(OWNER_CONSOLE_BUSINESS_CACHE_KEY);
    window.localStorage.removeItem(OWNER_CONSOLE_PLATFORM_CACHE_KEY);
  } catch (_) {}
}

function ownerConsoleWithTimeout(promise, timeoutMs, fallbackValue = null) {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((resolve) => {
      timer = setTimeout(() => resolve(fallbackValue), timeoutMs);
    }),
  ]).finally(() => {
    try { if (timer) clearTimeout(timer); } catch (_) {}
  });
}

function ownerConsoleLimitRows(rows, maxRows) {
  const arr = Array.isArray(rows) ? rows : [];
  if (!maxRows || arr.length <= maxRows) return arr;
  return arr.slice(0, maxRows);
}

function ownerConsoleNormalizeDoc(d, extra = {}) {
  return { id: d.id, path: d.ref?.path || extra.path || d.id, ...extra, ...(d.data() || {}) };
}

function ownerConsoleCacheAgeLabel(savedAt) {
  const ms = Number(savedAt || 0);
  if (!ms) return "cache local";
  const diff = Math.max(0, Date.now() - ms);
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return "cache reciente";
  if (minutes === 1) return "cache de 1 min";
  return `cache de ${minutes} min`;
}

export default function OwnerConsolePage({ db = defaultDb, auth = defaultAuth, user = null }) {
  const [activeTab, setActiveTab] = useState("businesses");
  const [financeView,setFinanceView] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [analyticsEvents, setAnalyticsEvents] = useState([]);
  const [dataHealth, setDataHealth] = useState(null);
  const [platformDataLoaded, setPlatformDataLoaded] = useState(false);
  const [businessFilter, setBusinessFilter] = useState("pending");
  const [businessSearch, setBusinessSearch] = useState("");
  const [analyticsRange, setAnalyticsRange] = useState("month");
  const [analyticsBusinessId, setAnalyticsBusinessId] = useState("all");
  const [analyticsSource, setAnalyticsSource] = useState("all");
  const [showDataHealth, setShowDataHealth] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [openBusinessMenuId, setOpenBusinessMenuId] = useState("");
  const businessMenuAnchor = useRef(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [reviewAction,setReviewAction] = useState("reject");
  const [rejectError, setRejectError] = useState("");
  const businessMutationVersionRef = useRef(0);
  const [businessAnalytics, setBusinessAnalytics] = useState({ id: null, rows: [], loading: false, error: "", limited: false });
  const [businessAnalyticsRefresh, setBusinessAnalyticsRefresh] = useState(0);
  const effectiveIdentity = getLoopEffectiveAuthIdentity(auth, user);
  const currentEmail = normalizeEmail(effectiveIdentity?.email);
  const currentUid = String(effectiveIdentity?.uid || "").trim();
  const canView = isLoopPlatformOwnerIdentity(effectiveIdentity);

  const businessIdsKey = businesses.map(b => b.id).sort().join("|");
  useEffect(() => {
    if (!canView || activeTab !== "analytics" || !businessIdsKey) return;
    let cancelled = false;
    setBusinessAnalytics({ id: "all", rows: [], loading: true, error: "", limited: false });
    const ids = businessIdsKey.split("|");
    const read = async () => {
      const rows = []; let limited = false;
      // Four concurrent reads, once per refresh; all filters reuse this same base.
      for (let i = 0; i < ids.length; i += 4) {
        if (cancelled) return;
        const results = await Promise.all(ids.slice(i, i + 4).map(async id => {
          const snapshot = await ownerConsoleWithTimeout(getDocs(query(collection(db, "businesses", id, "bookings"), limit(1801))), 12000, null);
          if (!snapshot) throw new Error("No se completó la consulta de todos los negocios.");
          if (snapshot.size >= 1801) limited = true;
          return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id, path: doc.ref.path, businessId: id }));
        }));
        rows.push(...results.flat());
      }
      if (cancelled) return;
      setBookings(rows);
      setPlatformDataLoaded(true);
      setDataHealth(buildOwnerDataHealth({ businesses: ids.map(id => ({ id })), bookings: rows }));
      setBusinessAnalytics({ id: "all", rows, loading: false, error: "", limited });
    };
    read().catch(error => {
      if (!cancelled) setBusinessAnalytics({ id: "all", rows: [], loading: false, error: ownerConsoleErrorMessage(error), limited: false });
    });
    return () => { cancelled = true; };
  }, [canView, activeTab, businessIdsKey, businessAnalyticsRefresh, db]);
  const analyticsRequestIdRef = useRef(0);

  const loadConsoleData = async (options = {}) => {
    if (!canView) {
      setLoading(false);
      return;
    }

    const includePlatformData = options && options.includePlatformData === true;
    const analyticsRequestId = includePlatformData ? ++analyticsRequestIdRef.current : 0;
    const mutationVersionAtStart = businessMutationVersionRef.current;
    const force = options && options.force === true;
    const useCache = options && options.useCache !== false;
    const silent = options && options.silent === true;
    const nextErrors = [];
    let cacheWasShown = false;

    if (!silent) setError("");
    if (!silent) setLoading(true);

    const applyBusinessCache = () => {
      if (!useCache || force) return false;
      const cached = readOwnerConsoleCache(OWNER_CONSOLE_BUSINESS_CACHE_KEY, OWNER_CONSOLE_BUSINESS_CACHE_TTL_MS);
      const cachedBusinesses = Array.isArray(cached?.businesses) ? cached.businesses : [];
      if (!cachedBusinesses.length) return false;
      setBusinesses(cachedBusinesses);
      if (!silent) setLoading(false);
      cacheWasShown = true;
      return true;
    };

    const applyPlatformCache = () => {
      if (!useCache || force) return false;
      const cached = readOwnerConsoleCache(OWNER_CONSOLE_PLATFORM_CACHE_KEY, OWNER_CONSOLE_PLATFORM_CACHE_TTL_MS);
      if (!cached || !Array.isArray(cached.businesses)) return false;
      const cachedBusinesses = Array.isArray(cached.businesses) ? cached.businesses : [];
      const cachedBookings = Array.isArray(cached.bookings) ? cached.bookings : [];
      const cachedAnalytics = Array.isArray(cached.analyticsEvents) ? cached.analyticsEvents : [];
      setBusinesses(cachedBusinesses);
      setBookings(cachedBookings);
      setAnalyticsEvents(cachedAnalytics);
      setPlatformDataLoaded(true);
      setDataHealth(cached.dataHealth || buildOwnerDataHealth({
        businesses: cachedBusinesses,
        bookings: cachedBookings,
        analyticsEvents: cachedAnalytics,
        serverHealth: null,
        errors: [{ source: "local_cache", message: ownerConsoleCacheAgeLabel(cached.savedAt) }],
      }));
      if (!silent) setLoading(false);
      cacheWasShown = true;
      return true;
    };

    if (includePlatformData) applyPlatformCache();
    else applyBusinessCache();

    const finishLoading = () => {
      if (!silent && !cacheWasShown) setLoading(false);
    };

    const loadBusinessesFast = async () => {
      // First choice for daily owner work: verified Admin SDK HTTP reader.
      // It accepts the effective manual-auth token on iOS and does not depend on
      // Firestore client-rule deployment being perfectly in sync.
      try {
        const result = await callLoopOwnerBusinessAdmin({
          db,
          auth,
          user,
          action: "listBusinesses",
          limit: OWNER_CONSOLE_CLIENT_LIMITS.businesses,
        });
        const serverRows = Array.isArray(result?.businesses) ? result.businesses : [];
        const businessList = serverRows
          .map((row) => ({ ...(row || {}), id: row?.id || row?.businessId || "" }))
          .filter((row) => row.id)
          .sort((a, b) => toMs(b.createdAt || b.updatedAt) - toMs(a.createdAt || a.updatedAt));
        setBusinesses(businessList);
        writeOwnerConsoleCache(OWNER_CONSOLE_BUSINESS_CACHE_KEY, { businesses: businessList });
        return businessList;
      } catch (serverError) {
        console.warn("[OwnerConsole] owner HTTP business reader unavailable; using Firestore client fallback", serverError);
      }

      const snap = await getDocs(query(collection(db, "businesses"), limit(OWNER_CONSOLE_CLIENT_LIMITS.businesses)));
      const businessList = snap.docs
        .map((d) => ownerConsoleNormalizeDoc(d))
        .sort((a, b) => toMs(b.createdAt || b.updatedAt) - toMs(a.createdAt || a.updatedAt));
      setBusinesses(businessList);
      writeOwnerConsoleCache(OWNER_CONSOLE_BUSINESS_CACHE_KEY, { businesses: businessList });
      return businessList;
    };

    // Daily owner work should never wait on platform analytics.
    if (!includePlatformData) {
      try {
        await loadBusinessesFast();
      } catch (err) {
        console.error("[OwnerConsole] fast businesses load failed", err);
        if (!cacheWasShown) {
          setBusinesses([]);
          setError("No pude cargar empresas. Revisa permisos de Firestore.");
        } else {
          setError("Mostré empresas desde cache local, pero no pude refrescar Firestore ahora.");
        }
      } finally {
        finishLoading();
      }
      return;
    }

    // First choice: server-side owner reader, but never let it block the page for 30+ seconds.
    try {
      const fn = httpsCallable(getFunctions(), "ownerListPlatformData");
      const result = await ownerConsoleWithTimeout(
        fn({ includeAnalytics: true, maxDocs: OWNER_CONSOLE_CLIENT_LIMITS.bookings, analyticsMaxDocs: OWNER_CONSOLE_CLIENT_LIMITS.analyticsEvents }),
        OWNER_CONSOLE_SERVER_TIMEOUT_MS,
        { __ownerTimeout: true }
      );

      if (result && result.__ownerTimeout) {
        throw new Error(`ownerListPlatformData timeout after ${OWNER_CONSOLE_SERVER_TIMEOUT_MS}ms`);
      }

      const data = result?.data || {};
      const businessList = ownerNormalizeServerRows(data.businesses)
        .sort((a, b) => toMs(b.createdAt || b.updatedAt) - toMs(a.createdAt || a.updatedAt));
      const rawBookingRows = ownerNormalizeServerRows(data.bookings);
      const bookingRows = ownerFilterTrustedBookings(rawBookingRows, businessList);
      const analyticsRows = ownerNormalizeServerRows(data.analyticsEvents);
      const health = buildOwnerDataHealth({
        businesses: businessList,
        bookings: bookingRows,
        analyticsEvents: analyticsRows,
        serverHealth: data.health || null,
        errors: [
          ...(data.health?.errors || []),
          ...(rawBookingRows.length !== bookingRows.length ? [{ source: "trusted_booking_filter", message: `${rawBookingRows.length - bookingRows.length} docs omitidos por negocio/fecha inválida` }] : []),
        ],
      });

      // A long analytics request must never undo a newer approval/rejection.
      if (analyticsRequestId === analyticsRequestIdRef.current && mutationVersionAtStart === businessMutationVersionRef.current) {
        setBusinesses(businessList);
      }
      if (analyticsRequestId !== analyticsRequestIdRef.current) return;
      setBookings(bookingRows);
      setAnalyticsEvents(analyticsRows);
      setPlatformDataLoaded(true);
      setDataHealth(health);
      writeOwnerConsoleCache(OWNER_CONSOLE_BUSINESS_CACHE_KEY, { businesses: businessList });
      writeOwnerConsoleCache(OWNER_CONSOLE_PLATFORM_CACHE_KEY, { businesses: businessList, bookings: bookingRows, analyticsEvents: analyticsRows, dataHealth: health });
      finishLoading();
      return;
    } catch (err) {
      const msg = ownerConsoleErrorMessage(err);
      console.warn("[OwnerConsole] ownerListPlatformData unavailable, using fast client reader", err);
      nextErrors.push(`Cloud Function lenta/no disponible: ${msg}. Usando lector rápido del navegador.`);
    }

    const readWithinDeadline = async (reference) => {
      const snapshot = await ownerConsoleWithTimeout(getDocs(reference), 8000, null);
      if (!snapshot) throw new Error("Consulta incompleta: tiempo de espera agotado");
      return snapshot;
    };
    const health = {
      loadedAt: new Date().toISOString(),
      source: "client_reader_fast_fallback",
      businessesLoaded: 0,
      bookingsLoaded: 0,
      collectionGroupBookings: 0,
      businessFallbackBookings: 0,
      rootFallbackBookings: 0,
      analyticsEventsLoaded: 0,
      platformEventsLoaded: 0,
      businessAnalyticsEventsLoaded: 0,
      invalidDateBookings: 0,
      missingSourceBookings: 0,
      pathRoots: {},
      methodsUsed: [],
      errors: [],
    };

    let businessList = [];

    try {
      businessList = await loadBusinessesFast();
      health.businessesLoaded = businessList.length;
    } catch (err) {
      const msg = ownerConsoleErrorMessage(err);
      console.error("[OwnerConsole] businesses load failed", err);
      nextErrors.push("No pude cargar businesses. Revisa reglas de Firestore para el owner.");
      health.errors.push({ source: "businesses", message: msg });
    }

    const bookingMap = new Map();
    const addBookings = (items = [], method = "unknown") => {
      let added = 0;
      ownerConsoleLimitRows(ownerFilterTrustedBookings(items, businessList), OWNER_CONSOLE_CLIENT_LIMITS.bookings).forEach((booking) => {
        const key = ownerBookingDedupeKey(booking);
        if (!key || bookingMap.has(key)) return;
        bookingMap.set(key, booking);
        added += 1;
      });
      if (added > 0 && !health.methodsUsed.includes(method)) health.methodsUsed.push(method);
      return added;
    };

    let collectionGroupWorked = false;
    try {
      const snap = await ownerConsoleWithTimeout(
        readWithinDeadline(query(collectionGroup(db, "bookings"), limit(OWNER_CONSOLE_CLIENT_LIMITS.bookings))),
        6500,
        null
      );
      if (!snap) throw new Error("collectionGroup(bookings) timeout");
      const rows = snap.docs.map((d) => ownerConsoleNormalizeDoc(d));
      health.collectionGroupBookings = rows.length;
      addBookings(rows, "collectionGroup(bookings)");
      collectionGroupWorked = true;
    } catch (err) {
      const msg = ownerConsoleErrorMessage(err);
      console.warn("[OwnerConsole] collectionGroup bookings failed", err);
      health.errors.push({ source: "collectionGroup(bookings)", message: msg });
    }

    // Only use per-business subcollection fallback when collectionGroup is unavailable.
    // It is parallelized and capped so one slow business cannot freeze the console.
    if (!collectionGroupWorked && businessList.length) {
      const candidates = businessList.slice(0, OWNER_CONSOLE_CLIENT_LIMITS.businessFallbackBusinesses);
      const results = await Promise.allSettled(candidates.map(async (biz) => {
        const businessId = String(biz?.id || "").trim();
        if (!businessId) return [];
        const snap = await readWithinDeadline(query(collection(db, "businesses", businessId, "bookings"), limit(OWNER_CONSOLE_CLIENT_LIMITS.businessFallbackBookings)));
        return snap.docs.map((d) => ({
          id: d.id,
          path: d.ref.path,
          businessId,
          businessName: getBusinessName(biz),
          ...(d.data() || {}),
        }));
      }));
      results.forEach((result, idx) => {
        if (result.status === "fulfilled") {
          const rows = Array.isArray(result.value) ? result.value : [];
          health.businessFallbackBookings += rows.length;
          addBookings(rows, "businesses/{businessId}/bookings");
        } else {
          const biz = candidates[idx];
          health.errors.push({ source: `businesses/${biz?.id || idx}/bookings`, message: ownerConsoleErrorMessage(result.reason) });
        }
      });
    }

    // Root-level booking collections are legacy/noisy. Only use them if the trusted business booking readers returned nothing.
    if (bookingMap.size === 0) {
      const rootResults = await Promise.allSettled(["bookings", "reservations", "appointments"].map(async (collectionName) => {
        const snap = await readWithinDeadline(query(collection(db, collectionName), limit(250)));
        return { collectionName, rows: snap.docs.map((d) => ({ id: d.id, path: `${collectionName}/${d.id}`, ...(d.data() || {}) })) };
      }));
      rootResults.forEach((result) => {
        if (result.status === "fulfilled") {
          health.rootFallbackBookings += result.value.rows.length;
          addBookings(result.value.rows, `${result.value.collectionName} legacy fallback`);
        } else {
          health.errors.push({ source: "root booking collections", message: ownerConsoleErrorMessage(result.reason) });
        }
      });
    }

    const bookingSources = Array.from(bookingMap.values());
    health.bookingsLoaded = bookingSources.length;
    bookingSources.forEach((booking) => {
      if (!ownerBookingHasValidDate(booking)) health.invalidDateBookings += 1;
      if (!ownerBookingHasSource(booking)) health.missingSourceBookings += 1;
      const root = ownerPathRoot(booking.path);
      health.pathRoots[root] = (health.pathRoots[root] || 0) + 1;
    });
    if (analyticsRequestId !== analyticsRequestIdRef.current) return;
    setBookings(bookingSources);

    const analyticsMap = new Map();
    const addAnalytics = (items = []) => {
      ownerConsoleLimitRows(items, OWNER_CONSOLE_CLIENT_LIMITS.analyticsEvents).forEach((event) => {
        const key = String(event?.path || event?.id || JSON.stringify([event?.type, event?.businessId, event?.createdAt, event?.visitorId]));
        if (!key || analyticsMap.has(key)) return;
        analyticsMap.set(key, event);
      });
    };

    const analyticsResults = await Promise.allSettled([
      readWithinDeadline(query(collection(db, "platformEvents"), limit(OWNER_CONSOLE_CLIENT_LIMITS.analyticsEvents))).then((snap) => ({ source: "platformEvents", rows: snap.docs.map((d) => ownerConsoleNormalizeDoc(d)) })),
      readWithinDeadline(query(collectionGroup(db, "analyticsEvents"), limit(OWNER_CONSOLE_CLIENT_LIMITS.analyticsEvents))).then((snap) => ({ source: "collectionGroup(analyticsEvents)", rows: snap.docs.map((d) => ownerConsoleNormalizeDoc(d)) })),
    ]);

    analyticsResults.forEach((result) => {
      if (result.status === "fulfilled") {
        if (result.value.source === "platformEvents") health.platformEventsLoaded = result.value.rows.length;
        if (result.value.source === "collectionGroup(analyticsEvents)") health.businessAnalyticsEventsLoaded = result.value.rows.length;
        addAnalytics(result.value.rows);
      } else {
        health.errors.push({ source: "analytics", message: ownerConsoleErrorMessage(result.reason) });
      }
    });

    const analyticsSources = Array.from(analyticsMap.values());
    health.analyticsEventsLoaded = analyticsSources.length;
    if (analyticsRequestId !== analyticsRequestIdRef.current) return;
    setAnalyticsEvents(analyticsSources);
    setPlatformDataLoaded(true);
    setDataHealth(health);
    writeOwnerConsoleCache(OWNER_CONSOLE_PLATFORM_CACHE_KEY, { businesses: businessList, bookings: bookingSources, analyticsEvents: analyticsSources, dataHealth: health });

    if (nextErrors.length && !cacheWasShown) setError(nextErrors.join(" "));
    finishLoading();
  };

  const runHistoricalManualBackfill = async () => {
    if (!canView || backfillBusy) return;

    const currentMissing = Number(dataHealth?.missingSourceBookings || 0);
    const message = currentMissing > 0
      ? `Esto marcará como admin_manual histórico las reservas sin origen claro, sin señales online, dentro de businesses/{businessId}/bookings. Detectadas ahora: ${currentMissing}. ¿Continuar?`
      : "Esto buscará reservas históricas sin origen claro y las marcará como admin_manual si no tienen señales online. ¿Continuar?";

    if (typeof window !== "undefined" && !window.confirm(message)) return;

    setBackfillBusy(true);
    setBackfillResult(null);

    try {
      const fn = httpsCallable(getFunctions(), "ownerBackfillHistoricalManualBookings");
      const result = await fn({
        dryRun: false,
        limit: 5000,
        businessId: analyticsBusinessId !== "all" ? analyticsBusinessId : null,
      });
      const data = result?.data || {};
      setBackfillResult(data);
      setBusinessAnalyticsRefresh(n => n + 1);
    } catch (err) {
      const msg = ownerConsoleErrorMessage(err);
      setBackfillResult({ ok: false, error: msg });
    } finally {
      setBackfillBusy(false);
    }
  };

  useEffect(() => {
    loadConsoleData({ includePlatformData: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);


  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const requestedBusinessId = String(params.get("businessId") || "").trim();
      if (requestedBusinessId) {
        setBusinessFilter("all");
        setBusinessSearch(requestedBusinessId);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      try {
        if (event?.target?.closest?.(".owner-business-menu-wrap,.owner-business-menu-portal")) return;
      } catch (_) {}
      setOpenBusinessMenuId("");
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setOpenBusinessMenuId("");
      if (rejectTarget) {
        setRejectTarget(null);
        setRejectReason("");
        setRejectError("");
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [rejectTarget]);




  const metrics = useMemo(() => {
    const todayStart = startOfLocalDay().getTime();
    const thisMonth = monthKeyFromMs(Date.now());

    const businessCounts = businesses.reduce((acc, biz) => {
      if (isBusinessTrashed(biz)) return acc;
      const status = approvalStatus(biz);
      acc.total += 1;
      if (status === "approved") acc.approved += 1;
      else if (status === "rejected") acc.rejected += 1;
      else if (status === "pending") acc.pending += 1;
      if (biz.visible !== false && biz.hidden !== true && biz.isActive !== false) acc.visible += 1;
      return acc;
    }, { total: 0, approved: 0, pending: 0, rejected: 0, visible: 0 });

    const metricStart = ownerMetricRangeStart();
    const todayEnd = endOfLocalDay().getTime();
    const bookingCounts = bookings.reduce((acc, booking) => {
      const ms = getBookingDateMs(booking);
      if (!ms) return acc;
      acc.all += 1;
      if (ms >= metricStart && ms <= Date.now() + 1000 * 60 * 60 * 24 * 370) acc.total += 1;
      if (ms >= todayStart && ms <= todayEnd) acc.today += 1;
      if (monthKeyFromMs(ms) === thisMonth) acc.thisMonth += 1;
      const businessId = String(booking.businessId || booking.bizId || booking.business?.id || "sin negocio");
      acc.byBusiness[businessId] = (acc.byBusiness[businessId] || 0) + 1;
      const day = dayKeyFromMs(ms);
      acc.byDay[day] = (acc.byDay[day] || 0) + 1;
      return acc;
    }, { all: 0, total: 0, today: 0, thisMonth: 0, byBusiness: {}, byDay: {} });

    const topBusinesses = Object.entries(bookingCounts.byBusiness)
      .map(([businessId, count]) => {
        const biz = businesses.find((item) => String(item.id) === String(businessId));
        return { businessId, count, name: biz ? getBusinessName(biz) : businessId };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);

    const last7Days = Array.from({ length: 7 }, (_, idx) => {
      const d = startOfLocalDay();
      d.setDate(d.getDate() - (6 - idx));
      const key = dayKeyFromMs(d.getTime());
      return { key, label: d.toLocaleDateString("es-PA", { weekday: "short", day: "2-digit" }), count: bookingCounts.byDay[key] || 0 };
    });

    return { businessCounts, bookingCounts, topBusinesses, last7Days };
  }, [businesses, bookings]);

  const analyticsData = useMemo(() => {
    const range = ownerRangeBounds(analyticsRange);
    const rangeStart = range.start;
    const monthEnd = range.end - 1;
    const previousWindow = range.previous;
    const now = Date.now();
    const preparedBookings = businessAnalytics.rows
      .map((booking) => ({
        ...booking,
        __ms: getBookingDateMs(booking),
        __source: getBookingSource(booking),
      }))
      .filter((booking) => {
        if (!booking.__ms) return false;
        if (booking.__ms > endOfLocalDay().getTime()) return false;
        if (analyticsBusinessId !== "all" && getBookingBusinessId(booking) !== String(analyticsBusinessId)) return false;
        if (analyticsSource !== "all" && booking.__source !== analyticsSource) return false;
        return true;
      });

    const safeBookings = preparedBookings.filter((booking) => {
      if (booking.__ms > monthEnd || (rangeStart && booking.__ms < rangeStart)) return false;
      return true;
    });

    const previousBookings = previousWindow
      ? preparedBookings.filter((booking) => booking.__ms >= previousWindow.start && booking.__ms < previousWindow.end)
      : [];

    const revenue = safeBookings.reduce((sum, booking) => sum + getBookingRevenueValue(booking), 0);
    const previousRevenue = previousBookings.reduce((sum, booking) => sum + getBookingRevenueValue(booking), 0);
    const avgTicket = safeBookings.length ? revenue / safeBookings.length : 0;
    const previousAvgTicket = previousBookings.length ? previousRevenue / previousBookings.length : 0;

    const bySource = safeBookings.reduce((acc, booking) => {
      const source = booking.__source || "unknown";
      const current = acc[source] || { count: 0, revenue: 0 };
      current.count += 1;
      current.revenue += getBookingRevenueValue(booking);
      acc[source] = current;
      return acc;
    }, {
      online: { count: 0, revenue: 0 },
      manual: { count: 0, revenue: 0 },
      manual_probable: { count: 0, revenue: 0 },
      unknown: { count: 0, revenue: 0 },
    });

    const onlineShare = safeBookings.length ? Math.round((bySource.online.count / safeBookings.length) * 100) : 0;
    const manualCount = bySource.manual.count + bySource.manual_probable.count;
    const manualRevenue = bySource.manual.revenue + bySource.manual_probable.revenue;
    const manualShare = safeBookings.length ? Math.round((manualCount / safeBookings.length) * 100) : 0;
    const manualProbableShare = safeBookings.length ? Math.round((bySource.manual_probable.count / safeBookings.length) * 100) : 0;

    const byStatus = safeBookings.reduce((acc, booking) => {
      const status = getBookingStatus(booking);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, { confirmed: 0, pending: 0, cancelled: 0, unknown: 0 });

    const byBusiness = safeBookings.reduce((acc, booking) => {
      const businessId = getBookingBusinessId(booking);
      const source = booking.__source || "unknown";
      const current = acc[businessId] || {
        businessId,
        name: getBookingBusinessName(booking, businesses),
        count: 0,
        revenue: 0,
        lastMs: 0,
        sourceCounts: { online: 0, manual: 0, manual_probable: 0, unknown: 0 },
      };
      current.count += 1;
      current.revenue += getBookingRevenueValue(booking);
      current.lastMs = Math.max(current.lastMs || 0, booking.__ms || 0);
      current.sourceCounts[source] = (current.sourceCounts[source] || 0) + 1;
      acc[businessId] = current;
      return acc;
    }, {});

    const byService = safeBookings.reduce((acc, booking) => {
      const service = getBookingServiceName(booking);
      const current = acc[service] || { service, count: 0, revenue: 0 };
      current.count += 1;
      current.revenue += getBookingRevenueValue(booking);
      acc[service] = current;
      return acc;
    }, {});

    const trendDays = buildOwnerTrend(safeBookings, rangeStart, new Date(monthEnd));

    const topBusinesses = Object.values(byBusiness)
      .map((item) => {
        const sourceEntries = Object.entries(item.sourceCounts || {}).sort((a, b) => b[1] - a[1]);
        const dominantSource = sourceEntries[0] ? sourceEntries[0][0] : "unknown";
        return { ...item, avgTicket: item.count ? item.revenue / item.count : 0, dominantSource, dominantSourceLabel: sourceGroupLabel(dominantSource) };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    const topServices = Object.values(byService).sort((a, b) => b.count - a.count).slice(0, 8);
    const latestBookings = safeBookings.slice().sort((a, b) => b.__ms - a.__ms).slice(0, 8);

    const safeEvents = analyticsEvents
      .map((event) => ({ ...event, __ms: getAnalyticsEventTimeMs(event), __type: getAnalyticsEventType(event), __businessId: getAnalyticsBusinessId(event) }))
      .filter((event) => {
        if (!event.__type) return false;
        if (event.__ms && rangeStart && event.__ms < rangeStart) return false;
        if (event.__ms && event.__ms > now + 1000 * 60 * 60 * 24 * 2) return false;
        if (analyticsBusinessId !== "all" && event.__businessId !== String(analyticsBusinessId)) return false;
        return true;
      });

    const eventCounts = safeEvents.reduce((acc, event) => {
      acc[event.__type] = (acc[event.__type] || 0) + 1;
      return acc;
    }, {});

    const businessViewsById = safeEvents
      .filter((event) => event.__type === "business_page_view" && event.__businessId)
      .reduce((acc, event) => {
        const current = acc[event.__businessId] || { businessId: event.__businessId, name: getBookingBusinessName(event, businesses), count: 0 };
        current.count += 1;
        acc[event.__businessId] = current;
        return acc;
      }, {});

    const topViewedBusinesses = Object.values(businessViewsById).sort((a, b) => b.count - a.count).slice(0, 8);
    const mainMenuViews = eventCounts.main_menu_view || 0;
    const businessPageViews = eventCounts.business_page_view || 0;
    const bookingStarts = (eventCounts.booking_started || 0) + (eventCounts.booking_opened || 0);
    const bookingCompletes = eventCounts.booking_completed || 0;
    const visitToStartRate = businessPageViews ? Math.round((bookingStarts / businessPageViews) * 100) : 0;
    const startToCompleteRate = bookingStarts ? Math.round((bookingCompletes / bookingStarts) * 100) : 0;

    const activeBusinessIds = new Set(safeBookings.map((booking) => getBookingBusinessId(booking)));
    const previousActiveBusinessIds = new Set(previousBookings.map((booking) => getBookingBusinessId(booking)));
    const conversionProxy = businesses.length ? Math.round((activeBusinessIds.size / Math.max(1, businesses.length)) * 100) : 0;
    const approvedBusinesses = businesses.filter((biz) => approvalStatus(biz) === "approved" && !isBusinessHidden(biz));
    const businessesWithoutBookings = approvedBusinesses.filter((biz) => !activeBusinessIds.has(String(biz.id))).map((biz) => ({ id: biz.id, name: getBusinessName(biz), owner: getBusinessOwner(biz) })).slice(0, 8);

    const deltas = {
      bookings: percentDelta(safeBookings.length, previousBookings.length),
      revenue: percentDelta(revenue, previousRevenue),
      avgTicket: percentDelta(avgTicket, previousAvgTicket),
      activeBusinesses: percentDelta(activeBusinessIds.size, previousActiveBusinessIds.size),
    };

    const insights = [];
    if (topBusinesses[0]) insights.push({ tone: "brand", title: "Negocio líder", text: `${topBusinesses[0].name} lidera con ${topBusinesses[0].count} reservas.` });
    if (safeBookings.length && manualShare >= 50) insights.push({ tone: "manual", title: "Operación manual", text: `${manualShare}% de reservas vienen de la agenda.` });
    if (safeBookings.length && onlineShare >= 20) insights.push({ tone: "online", title: "Canal online creciendo", text: `${onlineShare}% de reservas entran online.` });
    if (byStatus.pending > 0) insights.push({ tone: "warning", title: "Pendientes", text: `${byStatus.pending} reservas esperan confirmación.` });
    if (bySource.unknown.count > 0) insights.push({ tone: "warning", title: "Por clasificar", text: `${bySource.unknown.count} reservas aún no tienen origen claro.` });
    if (deltas.revenue !== null) insights.push({ tone: deltas.revenue >= 0 ? "positive" : "warning", title: "Ingresos", text: formatSignedPercent(deltas.revenue) });
    if (!insights.length) insights.push({ tone: "brand", title: "Sin señales críticas", text: "La data está limpia para este filtro." });

    const opportunities = [];
    if (businessesWithoutBookings.length) opportunities.push({ title: "Reactivar negocios sin reservas", text: `${businessesWithoutBookings.length} negocios aprobados no tienen reservas en este periodo.`, action: "Revisar top negocios" });
    if (byStatus.pending > 0) opportunities.push({ title: "Confirmar reservas pendientes", text: `${byStatus.pending} reservas podrían necesitar seguimiento.`, action: "Filtrar por pendientes" });
    if (bySource.unknown.count > 0) opportunities.push({ title: "Completar origen de reservas", text: `${bySource.unknown.count} reservas siguen por clasificar.`, action: "Usar diagnóstico" });
    if (topBusinesses[0] && safeBookings.length && compactPercent(topBusinesses[0].count, safeBookings.length) >= 60) opportunities.push({ title: "Concentración alta", text: `${topBusinesses[0].name} concentra ${compactPercent(topBusinesses[0].count, safeBookings.length)}% de reservas.`, action: "Diversificar adopción" });
    if (!opportunities.length) opportunities.push({ title: "Buen balance operativo", text: "No hay alertas fuertes en este filtro.", action: "Mantener seguimiento" });

    return {
      bookings: safeBookings,
      previousBookings,
      totalBookings: safeBookings.length,
      revenue,
      previousRevenue,
      avgTicket,
      previousAvgTicket,
      byStatus,
      bySource,
      onlineShare,
      manualShare,
      manualProbableShare,
      manualCount,
      manualRevenue,
      topBusinesses,
      topServices,
      trendDays,
      latestBookings,
      activeBusinessCount: activeBusinessIds.size,
      conversionProxy,
      businessesWithoutBookings,
      deltas,
      insights,
      opportunities,
      events: safeEvents,
      eventCounts,
      mainMenuViews,
      businessPageViews,
      bookingStarts,
      bookingCompletes,
      visitToStartRate,
      startToCompleteRate,
      topViewedBusinesses,
    };
  }, [analyticsBusinessId, analyticsRange, analyticsSource, analyticsEvents, bookings, businesses, businessAnalytics]);

  const exportAnalyticsCsv = () => {
    const rows = [
      ["Fecha", "Empresa", "Cliente", "Servicio", "Estado", "Origen", "Monto", "Path"],
      ...analyticsData.bookings.map((booking) => [
        formatDate(booking.__ms),
        getBookingBusinessName(booking, businesses),
        getBookingClientName(booking),
        getBookingServiceName(booking),
        getBookingStatus(booking),
        bookingSourceLabel(booking.__source),
        getBookingRevenueValue(booking),
        booking.path || booking.id || "",
      ]),
    ];

    const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `loop-analytics-${analyticsRange}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const businessFilterCounts = useMemo(() => {
    const base = { deleted: 0, all: businesses.filter(b => !isBusinessTrashed(b)).length, pending: 0, draft: 0, changes_requested: 0, approved: 0, rejected: 0, hidden: 0, featured: 0 };
    businesses.forEach((biz) => {
      if (isBusinessTrashed(biz)) { base.deleted += 1; return; }
      const status = approvalStatus(biz);
      if (base[status] != null) base[status] += 1;
      if (isBusinessHidden(biz)) base.hidden += 1;
      if (isBusinessFeatured(biz)) base.featured += 1;
    });
    return base;
  }, [businesses]);

  const filteredBusinesses = useMemo(() => {
    const q = String(businessSearch || "").trim().toLowerCase();
    return businesses.filter((biz) => {
      if (isBusinessTrashed(biz)) return businessFilter === "deleted" && (!q || businessSearchHaystack(biz).includes(q));
      if (businessFilter === "deleted") return false;
      const status = approvalStatus(biz);
      const matchesFilter =
        businessFilter === "all" ||
        (businessFilter === "hidden" ? isBusinessHidden(biz) : businessFilter === "featured" ? isBusinessFeatured(biz) : status === businessFilter);

      if (!matchesFilter) return false;
      if (!q) return true;
      return businessSearchHaystack(biz).includes(q);
    });
  }, [businessFilter, businessSearch, businesses]);

  const approveBusiness = async (businessId) => {
    setBusyId(businessId);
    setError("");
    try {
      const result = await callLoopOwnerBusinessAdmin({
        db,
        auth,
        user,
        action: "approve",
        businessId,
      });
      businessMutationVersionRef.current += 1;
      analyticsRequestIdRef.current += 1;
      clearOwnerConsoleCaches();
      setPlatformDataLoaded(false);
      setBusinesses((prev) => prev.map((biz) => (
        biz.id === businessId
          ? {
              ...biz,
              ...(result?.business || {}),
              status: "active",
              approvalStatus: "approved",
              approved: true,
              isApproved: true,
              isActive: true,
              visible: true,
              hidden: false,
              published: true,
              approvedBy: currentEmail,
              approvedByEmail: currentEmail,
            }
          : biz
      )));
      void loadConsoleData({ includePlatformData: false, force: true, useCache: false, silent: true });
    } catch (err) {
      console.error("[OwnerConsole] approve failed", err);
      setError(`No pude aprobar la empresa: ${ownerConsoleErrorMessage(err)}`);
    } finally {
      setBusyId("");
    }
  };

  const rejectBusiness = async (businessId, reasonInput) => {
    const reason = String(reasonInput || "").trim();
    if (!reason) {
      setRejectError("Escribe un motivo breve para rechazar esta empresa.");
      return false;
    }

    setBusyId(businessId);
    setError("");
    try {
      const result = await callLoopOwnerBusinessAdmin({
        db,
        auth,
        user,
        action: reviewAction,
        businessId,
        reason,
      });
      businessMutationVersionRef.current += 1;
      analyticsRequestIdRef.current += 1;
      clearOwnerConsoleCaches();
      setPlatformDataLoaded(false);
      setBusinesses((prev) => prev.map((biz) => (
        biz.id === businessId
          ? {
              ...biz,
              ...(result?.business || {}),
              status: reviewAction === "requestChanges" ? "changes_requested" : "rejected",
              approvalStatus: reviewAction === "requestChanges" ? "changes_requested" : "rejected",
              approved: false,
              isApproved: false,
              isActive: false,
              visible: false,
              hidden: true,
              published: false,
              rejectedBy: currentEmail,
              rejectedByEmail: currentEmail,
              rejectedReason: reason,
            }
          : biz
      )));
      setRejectTarget(null);
      setRejectReason("");
      setRejectError("");
      void loadConsoleData({ includePlatformData: false, force: true, useCache: false, silent: true });
      return true;
    } catch (err) {
      console.error("[OwnerConsole] reject failed", err);
      setError(`No pude rechazar la empresa: ${ownerConsoleErrorMessage(err)}`);
      return false;
    } finally {
      setBusyId("");
    }
  };

  const openRejectDialog = (biz,action="reject") => {
    setReviewAction(action);
    setOpenBusinessMenuId("");
    setRejectTarget(biz || null);
    setRejectReason("");
    setRejectError("");
  };

  const confirmRejectBusiness = async () => {
    if (!rejectTarget?.id || busyId) return;
    await rejectBusiness(rejectTarget.id, rejectReason);
  };

  const patchBusiness = async (businessId, patch, optimisticPatch, errorMessage) => {
    setBusyId(businessId);
    setError("");
    try {
      const result = await callLoopOwnerBusinessAdmin({
        db,
        auth,
        user,
        action: "patch",
        businessId,
        payload: patch,
      });
      businessMutationVersionRef.current += 1;
      analyticsRequestIdRef.current += 1;
      clearOwnerConsoleCaches();
      setPlatformDataLoaded(false);
      setBusinesses((prev) => prev.map((biz) => (
        biz.id === businessId ? { ...biz, ...(result?.business || {}), ...(optimisticPatch || patch) } : biz
      )));
      void loadConsoleData({ includePlatformData: false, force: true, useCache: false, silent: true });
    } catch (err) {
      console.error("[OwnerConsole] business patch failed", err);
      setError(errorMessage || `No pude actualizar esta empresa: ${ownerConsoleErrorMessage(err)}`);
    } finally {
      setBusyId("");
    }
  };

  const toggleBusinessTrash = async (biz) => {
    const restoring = isBusinessTrashed(biz);
    const message = restoring
      ? `¿Restaurar «${getBusinessName(biz)}»? Permanecerá oculto hasta que decidas publicarlo.`
      : `¿Mover «${getBusinessName(biz)}» a la papelera? Dejará de publicarse. Sus reservas, clientes y cobros se conservarán y podrás restaurarlo.`;
    if (!window.confirm(message)) return;
    setOpenBusinessMenuId("");
    const patch = restoring ? businessRestorePatch(biz, currentEmail) : businessTrashPatch(biz, currentEmail);
    await patchBusiness(biz.id, patch, patch, "No pude actualizar la papelera. Intenta nuevamente.");
  };

  const toggleBusinessVisibility = async (biz) => {
    const hidden = isBusinessHidden(biz);
    await patchBusiness(
      biz.id,
      {
        hidden: !hidden,
        visible: hidden,
        isActive: hidden,
        published: hidden,
      },
      {
        hidden: !hidden,
        visible: hidden,
        isActive: hidden,
        published: hidden,
      },
      hidden ? "No pude mostrar la empresa." : "No pude ocultar la empresa."
    );
  };

  const toggleBusinessFeatured = async (biz) => {
    const featured = isBusinessFeatured(biz);
    await patchBusiness(
      biz.id,
      {
        featured: !featured,
        isFeatured: !featured,
        recommended: !featured,
        featuredAt: !featured ? new Date().toISOString() : null,
      },
      {
        featured: !featured,
        isFeatured: !featured,
        recommended: !featured,
      },
      featured ? "No pude quitar destacado." : "No pude destacar la empresa."
    );
  };

  const copyBusinessLink = async (biz) => {
    const path = getBusinessPublicPath(biz);
    if (!path) return;
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch (_) {
      try { window.prompt("Copia el enlace del negocio:", url); } catch (__) {}
    }
    setOpenBusinessMenuId("");
  };

  const exportBusinessesCsv = () => {
    const rows = [
      ["Nombre", "Dueño", "Categoría", "Estado", "Visible", "Destacada", "Slug", "Creada"],
      ...filteredBusinesses.map((biz) => [
        getBusinessName(biz),
        getBusinessOwner(biz),
        getBusinessCategory(biz),
        statusLabel(approvalStatus(biz)),
        isBusinessHidden(biz) ? "No" : "Sí",
        isBusinessFeatured(biz) ? "Sí" : "No",
        getBusinessSlug(biz),
        formatDate(biz.createdAt || biz.updatedAt),
      ]),
    ];

    const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "loop-owner-businesses.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: "businesses", label: "Empresas", count: metrics.businessCounts.pending },
    { id: "analytics", label: "Analytics" },
    { id: "finance", label: "Finanzas" },
    { id: "paymentRequests", label: "Solicitudes de cobro" },
    { id: "conditions", label: "Condiciones" },
    { id: "settings", label: "Configuración" },
  ];

  if (!canView) {
    return (
      <main className="owner-console-page">
        <style>{styles}</style>
        <section className="owner-denied">
          <div className="owner-denied-logo">∞</div>
          <h1>Owner Console privado</h1>
          <p>Esta consola solo está disponible para {OWNER_EMAIL}.</p>
          <span>Sesión actual: {currentEmail || "sin sesión detectada"}</span>
        </section>
      </main>
    );
  }

  return (
    <main className="owner-console-page">
      <style>{styles}</style>

      <section className="owner-console-shell">
        <header className="owner-console-compact-head">
          <div className="owner-console-compact-title">
            <div className="owner-console-kicker">Loop Admin</div>
            <div className="owner-console-title-line">
              <h1>Administración de Loop</h1>
              <span>{metrics.businessCounts.total} negocios · {metrics.businessCounts.pending} {metrics.businessCounts.pending === 1 ? "pendiente" : "pendientes"}</span>
            </div>
          </div>

          <button
            type="button"
            className="owner-refresh-button"
            onClick={() => { setBusinessAnalyticsRefresh(n => n + 1); loadConsoleData({ includePlatformData: false, force: true, useCache: false }); }}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? "is-spinning" : ""} />
            {loading ? "Actualizando" : "Actualizar"}
          </button>
        </header>

        <section className={`owner-summary-strip ${activeTab !== "businesses" ? "admin-hide-business-summary" : ""}`} aria-label="Resumen de negocios">
          <div><span>Total</span><strong>{metrics.businessCounts.total}</strong></div>
          <div className="is-pending"><span>Pendientes</span><strong>{metrics.businessCounts.pending}</strong></div>
          <div className="is-active"><span>Activos</span><strong>{metrics.businessCounts.visible}</strong></div>

        </section>

        <aside className="owner-left-rail"><nav className="owner-tabs owner-tabs-compact" aria-label="Owner console tabs">
          {tabs.map((tab) => (
            <button key={tab.id} type="button" className={activeTab === tab.id ? "active" : ""} onClick={() => setActiveTab(tab.id)}>
              <span>{tab.label}</span>
              {tab.count ? <strong>{tab.count}</strong> : null}
            </button>
          ))}
        </nav>
        {activeTab === "analytics" && (<>             <div className="owner-analytics-toolbar owner-analytics-toolbar-clean">
              <div className="owner-analytics-control">
                <label htmlFor="owner-range-mode">Período</label>
                <select id="owner-range-mode" value={/^\d{4}-\d{2}$/.test(analyticsRange) ? "month" : analyticsRange} onChange={e => setAnalyticsRange(e.target.value)}>
                  <option value="month">Por mes</option><option value="7d">Últimos 7 días</option><option value="30d">Últimos 30 días</option><option value="90d">Últimos 90 días</option><option value="all">Todo el historial</option>
                </select>
                {(analyticsRange === "month" || /^\d{4}-\d{2}$/.test(analyticsRange)) && <div className="owner-month-picker">
                  <button type="button" aria-label="Mes anterior" onClick={() => setAnalyticsRange(shiftOwnerMonth(analyticsRange === "month" ? ownerCurrentMonth() : analyticsRange, -1))}>‹</button>
                  <input aria-label="Mes y año" type="month" value={analyticsRange === "month" ? ownerCurrentMonth() : analyticsRange} max={ownerCurrentMonth()} onChange={e => { if (/^\d{4}-(0[1-9]|1[0-2])$/.test(e.target.value) && e.target.value <= ownerCurrentMonth()) setAnalyticsRange(e.target.value); }} />
                  <button type="button" aria-label="Mes siguiente" disabled={analyticsRange === "month" || analyticsRange >= ownerCurrentMonth()} onClick={() => setAnalyticsRange(shiftOwnerMonth(analyticsRange, 1))}>›</button>
                </div>}
              </div>

              <div className="owner-analytics-control">
                <span>Negocio</span>
                <select value={analyticsBusinessId} onChange={(e) => setAnalyticsBusinessId(e.target.value)}>
                  <option value="all">Todos los negocios</option>
                  {businesses.map((biz) => (
                    <option key={biz.id} value={biz.id}>{getBusinessName(biz)}</option>
                  ))}
                </select>
              </div>

              <div className="owner-analytics-control">
                <span>Origen</span>
                <select value={analyticsSource} onChange={(e) => setAnalyticsSource(e.target.value)}>
                  <option value="all">Todos</option>
                  <option value="online">Online</option>
                  <option value="manual">Manual confirmado</option>
                  <option value="manual_probable">Manual probable</option>
                  <option value="unknown">Por clasificar</option>
                </select>
              </div>

              <button type="button" className="owner-export-btn" onClick={exportAnalyticsCsv} disabled={!analyticsData.bookings.length}>
                Exportar
              </button>
            </div>

 </>)}
        </aside>

        {error ? <div className="owner-error">{error}</div> : null}

        {activeTab === "businesses" ? (
          <section className="owner-panel owner-businesses-panel-compact">
            <div className="owner-businesses-heading">
              <div>
                <h2>Empresas</h2>
                <p>Aprueba solicitudes y administra los negocios de Loop.</p>
              </div>
              <span>{filteredBusinesses.length} en vista</span>
            </div>

            <div className="owner-business-toolbar-compact">
              <label className="owner-business-search-compact">
                <Search size={16} />
                <input
                  value={businessSearch}
                  onChange={(e) => setBusinessSearch(e.target.value)}
                  placeholder="Buscar nombre, dueño, categoría o ID"
                />
                {businessSearch ? (
                  <button type="button" aria-label="Limpiar búsqueda" onClick={() => setBusinessSearch("")}>
                    <X size={15} />
                  </button>
                ) : null}
              </label>

              <div className="owner-filter-pills-compact" aria-label="Filtrar empresas">
                {[
                  ["pending", "Por revisar", businessFilterCounts.pending],
                  ["changes_requested", "Necesitan ajustes", businessFilterCounts.changes_requested],
                  ["draft", "En preparación", businessFilterCounts.draft],
                  ["approved", "Aprobados", businessFilterCounts.approved],
                  ["rejected", "Rechazadas", businessFilterCounts.rejected],
                  ["hidden", "Ocultas", businessFilterCounts.hidden],
                  ["deleted", "Papelera", businessFilterCounts.deleted],
                  ["featured", "Destacadas", businessFilterCounts.featured],
                  ["all", "Todas", businessFilterCounts.all],
                ].map(([key, label, count]) => (
                  <button
                    key={key}
                    type="button"
                    className={businessFilter === key ? "active" : ""}
                    onClick={() => setBusinessFilter(key)}
                  >
                    <span>{label}</span>
                    <strong>{count}</strong>
                  </button>
                ))}
              </div>

              <button type="button" className="owner-export-compact" onClick={exportBusinessesCsv} disabled={!filteredBusinesses.length}>
                CSV
              </button>
            </div>

            {loading ? (
              <div className="owner-loading owner-loading-compact">Cargando empresas...</div>
            ) : filteredBusinesses.length ? (
              <div className="owner-business-rows">
                {filteredBusinesses.map((biz) => {
                  const status = approvalStatus(biz);
                  const publicPath = getBusinessPublicPath(biz);
                  const editPath = getBusinessEditPath(biz);
                  const hidden = isBusinessHidden(biz);
                  const featured = isBusinessFeatured(biz);
                  const image = getBusinessImage(biz);
                  const isPending = status === "pending";
                  const menuOpen = openBusinessMenuId === biz.id;

                  return (
                    <article className={`owner-business-row ${hidden ? "is-hidden" : ""}`} key={biz.id}>
                      <div className="owner-business-row-avatar">
                        {image ? (
                          <img
                            src={image}
                            alt=""
                            loading="lazy"
                            onError={(event) => { event.currentTarget.style.display = "none"; }}
                          />
                        ) : null}
                        <span>{getBusinessName(biz).slice(0, 1).toUpperCase()}</span>
                      </div>

                      <div className="owner-business-row-info">
                        <div className="owner-business-row-title">
                          <h3>{getBusinessName(biz)}</h3>
                          <span className={`owner-status ${statusClass(status)}`}>{statusLabel(status)}</span>
                          {featured ? <span className="owner-featured-chip">Destacada</span> : null}
                          {hidden ? <span className="owner-hidden-chip">{isBusinessTrashed(biz) ? "En papelera" : "Oculta"}</span> : null}
                        </div>
                        <p>{getBusinessCategory(biz)} · {getBusinessOwner(biz)}</p>
                        <small>Creada {formatDate(biz.createdAt || biz.updatedAt)} · ID {String(biz.id || "").slice(0, 10)}</small>
                        {status === 'draft' && <p>El propietario aún no ha enviado la solicitud a revisión.</p>}
                      </div>

                      <div className="owner-business-row-actions">
                        {['draft','changes_requested'].includes(status) && editPath && <a className="owner-row-action" href={editPath}>Revisar preparación</a>}
                        {isPending && editPath && <a className="owner-row-action" href={editPath}>Revisar perfil y requisitos</a>}
                        {isPending ? (
                          <>
                            <button
                              type="button"
                              className="owner-row-action approve"
                              onClick={() => approveBusiness(biz.id)}
                              disabled={busyId === biz.id}
                            >
                              <Check size={16} />
                              Aprobar y publicar
                            </button>
                            {biz.onboardingVersion === "business_draft_v3" && <button type="button" className="owner-row-action" disabled={busyId === biz.id} onClick={()=>openRejectDialog(biz,"requestChanges")}>Solicitar ajustes</button>}
                            <button
                              type="button"
                              className="owner-row-action reject"
                              onClick={() => openRejectDialog(biz)}
                              disabled={busyId === biz.id}
                            >
                              <X size={16} />
                              Rechazar
                            </button>
                          </>
                        ) : null}

                        <div className="owner-business-menu-wrap">
                          <button
                            type="button"
                            className="owner-more-button"
                            aria-label={`Más opciones para ${getBusinessName(biz)}`}
                            aria-expanded={menuOpen}
                            onClick={(event) => {businessMenuAnchor.current=event.currentTarget;setOpenBusinessMenuId((current) => current === biz.id ? "" : biz.id);}}
                          >
                            <MoreHorizontal size={20} />
                          </button>

                          {menuOpen ? (
                            <OwnerBusinessMenu anchor={businessMenuAnchor.current} onClose={()=>setOpenBusinessMenuId('')}>
                              <button type="button" className="danger" disabled={busyId === biz.id} onClick={() => toggleBusinessTrash(biz)}>{isBusinessTrashed(biz) ? "Restaurar negocio" : "Eliminar negocio"}</button>
                              {!isBusinessTrashed(biz) && <>
                              {!isPending && status !== "approved" && biz.onboardingVersion !== 'business_draft_v3' ? (
                                <button type="button" onClick={() => { setOpenBusinessMenuId(""); approveBusiness(biz.id); }}>
                                  <Check size={16} /> Aprobar negocio
                                </button>
                              ) : null}
                              {!isPending && status !== "rejected" && biz.onboardingVersion !== 'business_draft_v3' ? (
                                <button type="button" className="danger" onClick={() => openRejectDialog(biz)}>
                                  <X size={16} /> Rechazar negocio
                                </button>
                              ) : null}
                              {editPath ? (
                                <a href={editPath} role="menuitem"><Pencil size={16} /> Editar</a>
                              ) : null}
                              {publicPath ? (
                                <a href={publicPath} target="_blank" rel="noreferrer" role="menuitem"><ExternalLink size={16} /> Ver público</a>
                              ) : null}
                              <button type="button" disabled={biz.onboardingVersion === 'business_draft_v3' && status !== 'approved'} onClick={() => { setOpenBusinessMenuId(""); toggleBusinessVisibility(biz); }}>
                                {hidden ? <Eye size={16} /> : <EyeOff size={16} />}
                                {hidden ? "Mostrar negocio" : "Ocultar negocio"}
                              </button>
                              <button type="button" onClick={() => { setOpenBusinessMenuId(""); toggleBusinessFeatured(biz); }}>
                                {featured ? <StarOff size={16} /> : <Star size={16} />}
                                {featured ? "Quitar destaque" : "Destacar"}
                              </button>
                              {publicPath ? (
                                <button type="button" onClick={() => copyBusinessLink(biz)}>
                                  <Copy size={16} /> Copiar enlace
                                </button>
                              ) : null}
                              </>}
                            </OwnerBusinessMenu>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="owner-business-empty-compact">
                <Building2 size={24} />
                <strong>No hay empresas en esta vista</strong>
                <span>Cambia el filtro o limpia la búsqueda.</span>
              </div>
            )}
          </section>
        ) : null}

        {activeTab === "analytics" ? (
          <section className="owner-panel owner-analytics-panel-clean">
            <div className="owner-panel-head owner-analytics-head-clean">
              <div>
                <h2>Analytics</h2>
                <p>Reservas, ingresos y actividad de tus negocios.</p>
              </div>
              <span>{getDateRangeLabel(analyticsRange)}</span>
            </div>

            {dataHealth ? (
              <div className="owner-data-status-strip">
                <div>
                  <strong>{businessAnalytics.loading ? "Consultando negocios…" : businessAnalytics.error ? "No se pudo consultar el negocio" : businessAnalytics.limited ? "Datos parciales · límite de consulta" : "Misma base para todos los negocios"}</strong>
                  <span>
                    {analyticsData.totalBookings} reservas en este filtro

                  </span>
                </div>
                <button type="button" onClick={() => setShowDataHealth((prev) => !prev)}>
                  {showDataHealth ? "Ocultar diagnóstico" : "Ver diagnóstico"}
                </button>
              </div>
            ) : null}

            {showDataHealth && dataHealth ? (
              <div className="owner-data-health-panel owner-data-health-panel-collapsed">
                <div className="owner-data-health-head">
                  <div>
                    <strong>Diagnóstico avanzado</strong>
                    <span>{dataHealth.source === "cloud_function_ownerListPlatformData" ? "Cloud Function privada" : "Fallback del navegador"}</span>
                  </div>
                  <div className="owner-data-health-actions">
                    <button type="button" onClick={() => setBusinessAnalyticsRefresh(n => n + 1)} disabled={loading || backfillBusy}>
                      {loading ? "Revisando..." : "Revisar data"}
                    </button>
                    <button
                      type="button"
                      className="owner-data-health-backfill-btn"
                      onClick={runHistoricalManualBackfill}
                      disabled={loading || backfillBusy}
                    >
                      {backfillBusy ? "Marcando..." : "Marcar históricas manuales"}
                    </button>
                  </div>
                </div>

                <div className="owner-data-health-grid owner-data-health-grid-compact">
                  <div><span>Negocios</span><strong>{dataHealth.businessesLoaded}</strong></div>
                  <div><span>Reservas</span><strong>{dataHealth.bookingsLoaded}</strong></div>
                  <div><span>CollectionGroup</span><strong>{dataHealth.collectionGroupBookings}</strong></div>
                  <div><span>Fallback negocio</span><strong>{dataHealth.businessFallbackBookings}</strong></div>
                  <div><span>Sin fecha</span><strong>{dataHealth.invalidDateBookings}</strong></div>
                  <div><span>Por clasificar</span><strong>{dataHealth.missingSourceBookings}</strong></div>
                </div>

                <div className="owner-data-health-details">
                  <div>
                    <span>Métodos</span>
                    <p>{dataHealth.methodsUsed && dataHealth.methodsUsed.length ? dataHealth.methodsUsed.join(" · ") : "Ningún método devolvió reservas"}</p>
                  </div>
                  <div>
                    <span>Rutas</span>
                    <p>
                      {dataHealth.pathRoots && Object.keys(dataHealth.pathRoots).length
                        ? Object.entries(dataHealth.pathRoots).map(([k, v]) => `${k}: ${v}`).join(" · ")
                        : "Sin rutas detectadas"}
                    </p>
                  </div>
                  {dataHealth.errors && dataHealth.errors.length ? (
                    <div className="owner-data-health-errors">
                      <span>Alertas técnicas</span>
                      {dataHealth.errors.slice(0, 4).map((err, idx) => (
                        <p key={`${err.source}-${idx}`}>{err.source}: {err.message}</p>
                      ))}
                    </div>
                  ) : null}
                </div>

                {backfillResult ? (
                  <div className={`owner-backfill-result ${backfillResult.ok === false ? "is-error" : "is-success"}`}>
                    {backfillResult.ok === false ? (
                      <p>No pude completar el backfill: {backfillResult.error || "error desconocido"}</p>
                    ) : (
                      <p>
                        Backfill listo: {backfillResult.updated || 0} reservas marcadas como manuales históricas.
                        {backfillResult.skippedOnline ? ` Online omitidas: ${backfillResult.skippedOnline}.` : ""}
                        {backfillResult.skippedHasSource ? ` Ya tenían origen: ${backfillResult.skippedHasSource}.` : ""}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {(businessAnalytics.loading || !businessAnalytics.id || businessAnalytics.error) ? (
              <div role="status" className="owner-query-message">{businessAnalytics.error ? "No se pudo completar la consulta. Reintenta para ver los totales." : "Consultando las reservas de tus negocios…"}
                {businessAnalytics.error && <button onClick={() => setBusinessAnalyticsRefresh(n => n + 1)}>Reintentar</button>}
              </div>
            ) : !platformDataLoaded && analyticsBusinessId === "all" ? <p role="status">Cargando reservas…</p> : (<>
            {analyticsData.totalBookings === 0 && <p className="owner-query-message">No hay reservas en los datos consultados para este período y origen. Prueba otro rango o selecciona Todos los orígenes.</p>}
            <details className="owner-insights-panel"><summary>Lectura del período</summary>
              <div className="owner-insights-head">
                <div>
                  <span>Resumen inteligente</span>
                  <h3>Qué está pasando</h3>
                </div>
                <small>{getDateRangeLabel(analyticsRange)}</small>
              </div>
              <div className="owner-insights-grid">
                {analyticsData.insights.slice(0, 4).map((item, idx) => (
                  <article key={`${item.title}-${idx}`} className={`owner-insight-card tone-${item.tone || "default"}`}>
                    <strong>{item.title}</strong>
                    <p>{item.text}</p>
                  </article>
                ))}
              </div>
            </details>

            <div className="owner-analytics-metric-row owner-kpi-row-clean">
              <OwnerMetricCard label="Reservas" value={analyticsData.totalBookings} note={formatSignedPercent(analyticsData.deltas.bookings)} tone="brand" />
              <OwnerMetricCard label="Ingresos estimados" value={formatMoney(analyticsData.revenue)} note={formatSignedPercent(analyticsData.deltas.revenue)} />
              <OwnerMetricCard label="Ticket promedio" value={formatMoney(analyticsData.avgTicket)} note={formatSignedPercent(analyticsData.deltas.avgTicket)} />
              <OwnerMetricCard label="Negocios con reservas" value={analyticsData.activeBusinessCount} note={`${analyticsData.conversionProxy}% de negocios · ${formatSignedPercent(analyticsData.deltas.activeBusinesses)}`} />
            </div>

            <div className="owner-source-summary-grid">
              <article className="owner-source-summary-card is-online">
                <div>
                  <span>Online</span>
                  <strong>{analyticsData.bySource.online.count}</strong>
                </div>
                <small>{analyticsData.onlineShare}% · {formatMoney(analyticsData.bySource.online.revenue)}</small>
              </article>

              <article className="owner-source-summary-card is-manual">
                <div>
                  <span>Manual</span>
                  <strong>{analyticsData.manualCount}</strong>
                </div>
                <small>{analyticsData.manualShare}% · {formatMoney(analyticsData.manualRevenue)}</small>
              </article>

              <article className="owner-source-summary-card is-unknown">
                <div>
                  <span>Por clasificar</span>
                  <strong>{analyticsData.bySource.unknown.count}</strong>
                </div>
                <small>{formatMoney(analyticsData.bySource.unknown.revenue)}</small>
              </article>
            </div>

            <div className="owner-analytics-grid owner-analytics-grid-clean">
              <div className="owner-chart-card owner-wide-chart">
                <div className="owner-card-title-row">
                  <h3>Tendencia de reservas</h3>
                  <span>{analyticsData.totalBookings} reservas</span>
                </div>
                <div className="owner-bars owner-bars-v2 owner-bars-clean">
                  {analyticsData.trendDays.map((day) => {
                    const max = Math.max(...analyticsData.trendDays.map((d) => d.count), 1);
                    return (
                      <div key={day.key}>
                        <span>{day.label}</span>
                        <strong style={{ height: `${(day.count / max) * 120}px` }} title={`${day.count} reservas`} />
                        <em>{day.count}</em>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="owner-chart-card owner-status-card-clean">
                <div className="owner-card-title-row">
                  <h3>Estado</h3>
                </div>
                <div className="owner-status-breakdown owner-status-breakdown-clean">
                  {[
                    ["confirmed", "Confirmadas", analyticsData.byStatus.confirmed],
                    ["pending", "Pendientes", analyticsData.byStatus.pending],
                    ["cancelled", "Canceladas", analyticsData.byStatus.cancelled],
                    ["unknown", "Sin estado", analyticsData.byStatus.unknown],
                  ].map(([key, label, value]) => (
                    <div key={key}>
                      <span>{label}</span>
                      <strong>{value || 0}</strong>
                    </div>
                  ))}
                </div>
              </div>

              <div className="owner-chart-card owner-top-business-card">
                <h3>Top negocios</h3>
                {analyticsData.topBusinesses.length ? (
                  <div className="owner-business-performance-list">
                    {analyticsData.topBusinesses.slice(0, 6).map((item, idx) => (
                      <div key={`${item.businessId}-${idx}`}>
                        <span>{idx + 1}</span>
                        <div>
                          <strong>{item.name}</strong>
                          <small>{item.dominantSourceLabel} · última {formatDate(item.lastMs)}</small>
                        </div>
                        <em>{item.count} reservas</em>
                        <b>{formatMoney(item.revenue)}</b>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="owner-soft-copy">No hay reservas en este filtro.</p>
                )}
              </div>

              <div className="owner-chart-card">
                <h3>Top servicios</h3>
                {analyticsData.topServices.length ? (
                  <div className="owner-ranking owner-ranking-clean">
                    {analyticsData.topServices.map((item, idx) => (
                      <div key={`${item.service}-${idx}`}>
                        <span>{idx + 1}</span>
                        <strong>{item.service}</strong>
                        <em>{item.count}</em>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="owner-soft-copy">No hay servicios con reservas en este filtro.</p>
                )}
              </div>

              {analyticsData.mainMenuViews || analyticsData.businessPageViews || analyticsData.bookingStarts || analyticsData.bookingCompletes ? (
                <div className="owner-chart-card owner-web-activity-card">
                  <div className="owner-card-title-row">
                    <h3>Actividad web</h3>
                    <span>{analyticsData.startToCompleteRate}% conversión</span>
                  </div>
                  <div className="owner-web-activity-grid">
                    <div><span>Home</span><strong>{analyticsData.mainMenuViews}</strong></div>
                    <div><span>Perfiles</span><strong>{analyticsData.businessPageViews}</strong></div>
                    <div><span>Iniciadas</span><strong>{analyticsData.bookingStarts}</strong></div>
                    <div><span>Completadas</span><strong>{analyticsData.bookingCompletes}</strong></div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="owner-opportunities-panel">
              <div className="owner-card-title-row">
                <h3>Oportunidades</h3>
                <span>{analyticsData.opportunities.length} señales</span>
              </div>
              <div className="owner-opportunity-list">
                {analyticsData.opportunities.slice(0, 4).map((item, idx) => (
                  <article key={`${item.title}-${idx}`}>
                    <strong>{item.title}</strong>
                    <p>{item.text}</p>
                    <span>{item.action}</span>
                  </article>
                ))}
              </div>
            </div>

            <div className="owner-latest-bookings owner-latest-bookings-clean">
              <div className="owner-card-title-row">
                <h3>Últimas reservas</h3>
                <span>{analyticsData.latestBookings.length} recientes</span>
              </div>

              {analyticsData.latestBookings.length ? (
                <div className="owner-booking-table owner-booking-table-clean">
                  {analyticsData.latestBookings.map((booking) => (
                    <div key={`${booking.path || booking.id}-${booking.__ms}`}>
                      <strong>{getBookingClientName(booking)}</strong>
                      <span>{getBookingBusinessName(booking, businesses)}</span>
                      <span>{getBookingServiceName(booking)}</span>
                      <i className={`owner-source-pill ${bookingSourceClass(booking.__source)}`}>{bookingSourceLabel(booking.__source)}</i>
                      <em>{formatDate(booking.__ms)}</em>
                      <b>{formatMoney(getBookingRevenueValue(booking))}</b>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="owner-soft-copy">No hay reservas recientes para este filtro.</p>
              )}
            </div>
            </>)}
          </section>
        ) : null}

        {activeTab === "paymentRequests" && <section className="owner-panel"><OwnerPaymentGovernance businesses={businesses} mode="requests" /></section>}
        {activeTab === "conditions" && <><section className="owner-panel"><h2>Tarifas de cobro</h2><OwnerFinanceSettingsPanel businesses={businesses}/></section><section className="owner-panel"><OwnerPaymentGovernance businesses={businesses} mode="conditions" /></section></>}
        {activeTab === "finance" && <section className={"owner-panel finance-view-"+financeView}><h2>Finanzas</h2><nav className="admin-finance-tabs" aria-label="Secciones de finanzas">{[["summary","Resumen"],["movements","Movimientos"],["settlements","Liquidaciones"]].map(([key,label])=><button type="button" key={key} aria-pressed={financeView===key} onClick={()=>setFinanceView(key)}>{label}</button>)}</nav>{financeView === "settlements" ? <LoopSettlements owner embedded businesses={businesses}/> : <OwnerPlatformFinancialSummaryPanel db={db} businesses={businesses} view={financeView}/>}</section>}
        {activeTab === "settings" ? (
          <section className="owner-panel owner-settings-compact">
            <div className="owner-businesses-heading">
              <div>
                <h2>Configuración</h2>
                <p>Datos esenciales de esta consola privada.</p>
              </div>
            </div>
            <div className="owner-settings-list">
              <div><span>Owner autorizado</span><strong>{currentEmail || currentUid}</strong></div>
              <div><span>Correo de nuevas solicitudes</span><strong>{OWNER_EMAIL}</strong></div>
              <div><span>Flujo de publicación</span><strong>Aprobación manual</strong></div>
            </div>
          </section>
        ) : null}

        {rejectTarget ? (
          <div className="owner-modal-backdrop" role="presentation" onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busyId) {
              setRejectTarget(null);
              setRejectReason("");
              setRejectError("");
            }
          }}>
            <section className="owner-reject-modal" role="dialog" aria-modal="true" aria-labelledby="owner-reject-title">
              <button
                type="button"
                className="owner-modal-close"
                aria-label="Cerrar"
                disabled={Boolean(busyId)}
                onClick={() => { setRejectTarget(null); setRejectReason(""); setRejectError(""); }}
              >
                <X size={18} />
              </button>
              <span className="owner-modal-kicker">{reviewAction === "requestChanges" ? "Solicitar correcciones" : "Rechazar solicitud"}</span>
              <h2 id="owner-reject-title">{getBusinessName(rejectTarget)}</h2>
              <p>Escribe un motivo breve. Quedará guardado para mantener un historial claro.</p>
              <label>
                <span>{reviewAction === "requestChanges" ? "¿Qué debe corregir el negocio?" : "Motivo del rechazo"}</span>
                <textarea
                  autoFocus
                  value={rejectReason}
                  onChange={(event) => { setRejectReason(event.target.value); setRejectError(""); }}
                  placeholder="Ej.: Falta completar los datos de contacto del negocio."
                  rows={4}
                  maxLength={500}
                />
                <small>{rejectReason.length}/500</small>
              </label>
              {rejectError ? <div className="owner-modal-error">{rejectError}</div> : null}
              <div className="owner-modal-actions">
                <button type="button" className="secondary" disabled={Boolean(busyId)} onClick={() => { setRejectTarget(null); setRejectReason(""); setRejectError(""); }}>Cancelar</button>
                <button type="button" className="danger" disabled={Boolean(busyId)} onClick={confirmRejectBusiness}>
                  {busyId ? "Guardando..." : reviewAction === "requestChanges" ? "Enviar correcciones" : "Confirmar rechazo"}
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </section>
    </main>
  );
}

const styles = `
  .owner-console-page {
    min-height: calc(100dvh - 64px);
    background:
      radial-gradient(circle at 8% 0%, rgba(0, 103, 95, .10), transparent 30%),
      radial-gradient(circle at 94% 14%, rgba(20, 184, 166, .08), transparent 32%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 48%, #f8fafc 100%);
    color: #0f172a;
    box-sizing: border-box;
    padding: clamp(22px, 4vw, 44px) clamp(14px, 4vw, 42px) calc(42px + env(safe-area-inset-bottom, 0px));
  }

  .owner-console-shell {
    width: min(100%, 1420px);
    margin: 0 auto;
  }

  .owner-console-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 22px;
  }

  .owner-console-kicker {
    color: ${BRAND};
    font-size: 12px;
    font-weight: 950;
    letter-spacing: .15em;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  .owner-console-hero h1 {
    margin: 0;
    font-size: clamp(38px, 5vw, 72px);
    line-height: .9;
    letter-spacing: -.075em;
    font-weight: 950;
    color: #07111f;
  }

  .owner-console-hero p {
    max-width: 680px;
    margin: 16px 0 0;
    color: #64748b;
    font-size: 16px;
    line-height: 1.5;
    font-weight: 700;
  }

  .owner-session {
    margin-top: 14px;
    display: inline-flex;
    border: 1px solid rgba(0,103,95,.14);
    background: rgba(240,253,250,.76);
    color: ${BRAND};
    border-radius: 999px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 900;
  }

  .owner-console-actions button {
    min-height: 46px;
    border: 0;
    border-radius: 999px;
    padding: 0 18px;
    background: #0f172a;
    color: #fff;
    font-weight: 950;
    cursor: pointer;
    box-shadow: 0 14px 30px rgba(15,23,42,.16);
  }

  .owner-metrics-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin: 18px 0;
  }

  .owner-metric-card {
    border: 1px solid rgba(226,232,240,.92);
    border-radius: 24px;
    background: rgba(255,255,255,.82);
    box-shadow: 0 18px 46px rgba(15,23,42,.06);
    padding: 18px;
    min-height: 118px;
    display: grid;
    align-content: center;
  }

  .owner-metric-card span {
    color: #64748b;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: .12em;
    text-transform: uppercase;
  }

  .owner-metric-card strong {
    margin-top: 8px;
    color: #0f172a;
    font-size: 34px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: -.055em;
  }

  .owner-metric-card small {
    margin-top: 8px;
    color: #64748b;
    font-weight: 750;
  }

  .owner-metric-card.tone-brand {
    background: linear-gradient(135deg, #063f39 0%, #00675f 100%);
    border-color: transparent;
  }

  .owner-metric-card.tone-brand span,
  .owner-metric-card.tone-brand small {
    color: rgba(255,255,255,.72);
  }

  .owner-metric-card.tone-brand strong {
    color: #fff;
  }

  .owner-tabs {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 8px;
    border: 1px solid rgba(226,232,240,.92);
    border-radius: 999px;
    background: rgba(255,255,255,.74);
    box-shadow: 0 14px 34px rgba(15,23,42,.055);
    margin: 20px 0;
    -webkit-overflow-scrolling: touch;
  }

  .owner-tabs button {
    min-height: 42px;
    border: 0;
    border-radius: 999px;
    padding: 0 16px;
    background: transparent;
    color: #475569;
    font-weight: 950;
    white-space: nowrap;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .owner-tabs button.active {
    background: #0f172a;
    color: #fff;
    box-shadow: 0 10px 24px rgba(15,23,42,.16);
  }

  .owner-tabs button strong {
    min-width: 22px;
    height: 22px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${BRAND_2};
    color: #fff;
    font-size: 11px;
  }

  .owner-panel,
  .owner-embedded-early {
    border: 1px solid rgba(226,232,240,.92);
    border-radius: 32px;
    background: rgba(255,255,255,.88);
    box-shadow: 0 24px 70px rgba(15,23,42,.075);
    padding: clamp(18px, 3vw, 28px);
    overflow: hidden;
  }

  .owner-embedded-early .early-admin-page {
    min-height: 0;
    padding: 0;
    background: transparent;
  }

  .owner-embedded-early .early-admin-shell {
    max-width: none;
  }

  .owner-panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 18px;
  }

  .owner-panel-head h2 {
    margin: 0;
    font-size: clamp(26px, 3vw, 38px);
    line-height: 1;
    letter-spacing: -.055em;
    font-weight: 950;
  }

  .owner-panel-head p {
    margin: 8px 0 0;
    color: #64748b;
    font-weight: 700;
    line-height: 1.45;
  }

  .owner-panel-head > span {
    border-radius: 999px;
    background: rgba(0,103,95,.08);
    color: ${BRAND};
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 950;
    white-space: nowrap;
  }

  .owner-business-list {
    display: grid;
    gap: 10px;
  }

  .owner-business-card {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    gap: 14px;
    align-items: center;
    border: 1px solid #e2e8f0;
    border-radius: 22px;
    background: #fff;
    padding: 12px;
  }

  .owner-business-main {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .owner-business-avatar {
    width: 46px;
    height: 46px;
    border-radius: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, ${BRAND}, ${BRAND_2});
    color: #fff;
    font-weight: 950;
    flex: 0 0 auto;
  }

  .owner-business-main h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 950;
    color: #0f172a;
  }

  .owner-business-main p,
  .owner-business-main small {
    display: block;
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .owner-business-meta {
    display: grid;
    gap: 7px;
    justify-items: end;
    color: #64748b;
    font-size: 12px;
    font-weight: 850;
  }

  .owner-status,
  .owner-mini-table em {
    border-radius: 999px;
    padding: 6px 9px;
    font-size: 11px;
    line-height: 1;
    font-style: normal;
    font-weight: 950;
  }

  .owner-status.is-pending,
  .owner-mini-table em.is-pending {
    background: #fffbeb;
    color: #b45309;
  }

  .owner-status.is-approved,
  .owner-mini-table em.is-approved {
    background: #ecfdf5;
    color: #047857;
  }

  .owner-status.is-rejected,
  .owner-mini-table em.is-rejected {
    background: #fef2f2;
    color: #b91c1c;
  }

  .owner-business-actions {
    display: flex;
    gap: 8px;
  }

  .owner-business-actions button {
    height: 38px;
    border-radius: 999px;
    border: 0;
    padding: 0 13px;
    font-weight: 950;
    cursor: pointer;
  }

  .owner-business-actions .approve {
    background: ${BRAND};
    color: #fff;
  }

  .owner-business-actions .reject {
    background: #f1f5f9;
    color: #334155;
  }

  .owner-all-businesses {
    margin-top: 24px;
  }

  .owner-all-businesses h3,
  .owner-chart-card h3 {
    margin: 0 0 12px;
    font-size: 16px;
    font-weight: 950;
    color: #0f172a;
  }

  .owner-mini-table {
    display: grid;
    gap: 8px;
  }

  .owner-mini-table > div {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(180px, .6fr) auto;
    gap: 12px;
    align-items: center;
    border-top: 1px solid #eef2f7;
    padding: 10px 0;
  }

  .owner-mini-table strong {
    min-width: 0;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .owner-mini-table span {
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .owner-analytics-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(320px, .9fr);
    gap: 14px;
  }

  .owner-chart-card {
    border: 1px solid #e2e8f0;
    border-radius: 24px;
    background: #fff;
    padding: 16px;
  }

  .owner-bars {
    height: 180px;
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 8px;
    align-items: end;
  }

  .owner-bars > div {
    min-width: 0;
    display: grid;
    gap: 5px;
    justify-items: center;
    align-items: end;
  }

  .owner-bars strong {
    width: 100%;
    border-radius: 999px 999px 8px 8px;
    background: linear-gradient(180deg, ${BRAND_2}, ${BRAND});
    min-height: 8px;
  }

  .owner-bars span,
  .owner-bars em {
    color: #64748b;
    font-size: 10px;
    font-weight: 850;
    font-style: normal;
    text-align: center;
  }

  .owner-ranking {
    display: grid;
    gap: 8px;
  }

  .owner-ranking > div {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    border: 1px solid #eef2f7;
    border-radius: 16px;
    padding: 9px;
  }

  .owner-ranking span {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    background: #f1f5f9;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 950;
    color: #475569;
  }

  .owner-ranking strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .owner-ranking em {
    font-style: normal;
    font-weight: 950;
    color: ${BRAND};
  }


  .owner-data-health-panel {
    margin: 0 0 16px;
    border: 1px solid rgba(0,103,95,.16);
    border-radius: 24px;
    background:
      radial-gradient(circle at 0% 0%, rgba(0,137,127,.10), transparent 34%),
      linear-gradient(180deg, #ffffff 0%, #f8fffd 100%);
    padding: 14px;
    box-shadow: 0 16px 38px rgba(15,23,42,.055);
  }

  .owner-data-health-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 12px;
  }

  .owner-data-health-head strong {
    display: block;
    color: #0f172a;
    font-size: 16px;
    font-weight: 950;
    letter-spacing: -.03em;
  }

  .owner-data-health-head span {
    display: block;
    margin-top: 3px;
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
  }

  .owner-data-health-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
  }

  .owner-data-health-head button {
    min-height: 38px;
    border: 0;
    border-radius: 999px;
    padding: 0 14px;
    background: #00675f;
    color: #fff;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
  }

  .owner-data-health-head button.owner-data-health-backfill-btn {
    background: #0f172a;
  }

  .owner-data-health-head button:disabled {
    opacity: .62;
    cursor: not-allowed;
  }

  .owner-backfill-result {
    margin-top: 10px;
    border-radius: 16px;
    padding: 10px 12px;
    font-size: 12px;
    line-height: 1.35;
    font-weight: 850;
  }

  .owner-backfill-result p {
    margin: 0;
  }

  .owner-backfill-result.is-success {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #bbf7d0;
  }

  .owner-backfill-result.is-error {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }

  .owner-data-health-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }

  .owner-data-health-grid > div {
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: rgba(255,255,255,.86);
    padding: 10px;
  }

  .owner-data-health-grid span,
  .owner-data-health-details span {
    display: block;
    color: #64748b;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: .09em;
    text-transform: uppercase;
  }

  .owner-data-health-grid strong {
    display: block;
    margin-top: 6px;
    color: #0f172a;
    font-size: 22px;
    line-height: 1;
    font-weight: 950;
  }

  .owner-data-health-details {
    display: grid;
    gap: 8px;
    margin-top: 10px;
  }

  .owner-data-health-details > div {
    border-top: 1px solid rgba(226,232,240,.9);
    padding-top: 9px;
  }

  .owner-data-health-details p {
    margin: 5px 0 0;
    color: #475569;
    font-size: 12px;
    line-height: 1.35;
    font-weight: 780;
    overflow-wrap: anywhere;
  }

  .owner-data-health-errors p {
    color: #991b1b;
  }


  .owner-analytics-toolbar {
    display: grid;
    grid-template-columns: minmax(160px, .5fr) minmax(220px, 1fr) minmax(170px, .55fr) auto;
    gap: 12px;
    align-items: end;
    margin: 0 0 16px;
    border: 1px solid #e2e8f0;
    border-radius: 24px;
    background: rgba(248,250,252,.72);
    padding: 12px;
  }

  .owner-analytics-control {
    display: grid;
    gap: 7px;
  }

  .owner-analytics-control span {
    color: #64748b;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: .11em;
    text-transform: uppercase;
  }

  .owner-analytics-control select {
    width: 100%;
    min-height: 42px;
    border-radius: 15px;
    border: 1px solid #dbe5ee;
    background: #ffffff;
    padding: 0 13px;
    color: #0f172a;
    font-weight: 850;
    outline: none;
  }

  .owner-analytics-metric-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin: 0 0 16px;
  }

  .owner-analytics-grid-v2 {
    grid-template-columns: minmax(0, 1.15fr) minmax(320px, .85fr);
    align-items: stretch;
  }

  .owner-funnel-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin: 0 0 16px;
  }

  .owner-source-metric-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin: 0 0 16px;
  }

  .owner-source-card {
    border-radius: 22px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    padding: 16px;
    box-shadow: 0 12px 34px rgba(15,23,42,.045);
  }

  .owner-source-card span {
    color: #64748b;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: .11em;
    text-transform: uppercase;
  }

  .owner-source-card strong {
    display: block;
    margin-top: 8px;
    color: #0f172a;
    font-size: 30px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: -.055em;
  }

  .owner-source-card small {
    display: block;
    margin-top: 8px;
    color: #64748b;
    font-weight: 800;
  }

  .owner-source-card.is-online {
    border-color: rgba(0,103,95,.14);
    background: linear-gradient(180deg, #ffffff 0%, #f0fdfa 100%);
  }

  .owner-source-card.is-manual {
    border-color: rgba(100,116,139,.18);
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  }

  .owner-source-card.is-manual-probable {
    border-color: rgba(217,119,6,.18);
    background: linear-gradient(180deg, #ffffff 0%, #fffbeb 100%);
  }

  .owner-source-breakdown {
    display: grid;
    gap: 8px;
  }

  .owner-source-breakdown > div {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    gap: 10px;
    align-items: center;
    border: 1px solid #eef2f7;
    border-radius: 16px;
    padding: 11px 12px;
    background: #ffffff;
  }

  .owner-source-breakdown span {
    color: #64748b;
    font-size: 12px;
    font-weight: 850;
  }

  .owner-source-breakdown strong {
    color: #0f172a;
    font-size: 20px;
    font-weight: 950;
  }

  .owner-source-breakdown em {
    color: #00675f;
    font-size: 12px;
    font-style: normal;
    font-weight: 950;
  }

  .owner-source-breakdown .is-online,
  .owner-source-pill.is-online {
    background: #ecfdf5;
    color: #047857;
  }

  .owner-source-breakdown .is-manual,
  .owner-source-pill.is-manual {
    background: #f1f5f9;
    color: #334155;
  }

  .owner-source-breakdown .is-manual-probable,
  .owner-source-pill.is-manual-probable {
    background: #fffbeb;
    color: #b45309;
  }

  .owner-source-breakdown .is-unknown,
  .owner-source-pill.is-unknown {
    background: #fff7ed;
    color: #c2410c;
  }

  .owner-source-pill {
    width: fit-content;
    border-radius: 999px;
    padding: 5px 8px;
    font-size: 11px;
    line-height: 1;
    font-style: normal;
    font-weight: 950;
    white-space: nowrap;
  }

  .owner-wide-chart {
    grid-column: span 1;
  }

  .owner-card-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .owner-card-title-row h3 {
    margin: 0;
  }

  .owner-card-title-row span {
    border-radius: 999px;
    background: rgba(0,103,95,.08);
    color: #00675f;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .owner-bars-v2 {
    height: 210px;
    gap: 7px;
  }

  .owner-status-breakdown {
    display: grid;
    gap: 8px;
  }

  .owner-status-breakdown > div {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    align-items: center;
    border: 1px solid #eef2f7;
    border-radius: 16px;
    padding: 11px 12px;
    background: #ffffff;
  }

  .owner-status-breakdown span {
    color: #64748b;
    font-size: 12px;
    font-weight: 850;
  }

  .owner-status-breakdown strong {
    color: #0f172a;
    font-size: 20px;
    font-weight: 950;
  }

  .owner-latest-bookings {
    margin-top: 14px;
    border: 1px solid #e2e8f0;
    border-radius: 24px;
    background: #ffffff;
    padding: 16px;
  }

  .owner-latest-bookings h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 950;
  }

  .owner-booking-table {
    display: grid;
    gap: 0;
  }

  .owner-booking-table > div {
    display: grid;
    grid-template-columns: minmax(130px, .75fr) minmax(150px, .9fr) minmax(130px, .8fr) minmax(92px, .45fr) minmax(140px, .7fr) auto;
    gap: 10px;
    align-items: center;
    border-top: 1px solid #eef2f7;
    padding: 10px 0;
  }

  .owner-booking-table > div:first-child {
    border-top: 0;
  }

  .owner-booking-table strong,
  .owner-booking-table span,
  .owner-booking-table em,
  .owner-booking-table i {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .owner-booking-table strong {
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
  }

  .owner-booking-table span,
  .owner-booking-table em {
    color: #64748b;
    font-size: 12px;
    font-style: normal;
    font-weight: 750;
  }

  .owner-booking-table b {
    color: #00675f;
    font-size: 13px;
    font-weight: 950;
    text-align: right;
  }


  .owner-next-box,
  .owner-empty-panel {
    margin-top: 16px;
    border: 1px dashed rgba(148,163,184,.62);
    border-radius: 24px;
    background: rgba(248,250,252,.76);
    padding: 18px;
  }

  .owner-next-box strong {
    display: block;
    color: #0f172a;
    font-weight: 950;
    margin-bottom: 6px;
  }

  .owner-next-box p,
  .owner-soft-copy,
  .owner-empty-panel p {
    margin: 0;
    color: #64748b;
    font-weight: 700;
    line-height: 1.45;
  }

  .owner-empty-panel {
    text-align: center;
    padding: 34px 18px;
  }

  .owner-empty-icon,
  .owner-denied-logo {
    width: 54px;
    height: 54px;
    margin: 0 auto 12px;
    border-radius: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, ${BRAND}, ${BRAND_2});
    color: #fff;
    font-size: 28px;
    font-weight: 950;
  }

  .owner-empty-panel h3 {
    margin: 0 0 8px;
    font-size: 22px;
    letter-spacing: -.035em;
    font-weight: 950;
  }

  .owner-finance-roadmap {
    margin-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .owner-finance-roadmap span {
    border-radius: 999px;
    background: #fff;
    border: 1px solid #e2e8f0;
    padding: 9px 12px;
    color: #475569;
    font-size: 12px;
    font-weight: 900;
  }

  .owner-error {
    margin: 0 0 14px;
    border: 1px solid #fecaca;
    background: #fef2f2;
    color: #991b1b;
    border-radius: 18px;
    padding: 12px 14px;
    font-weight: 850;
  }

  .owner-loading {
    color: #64748b;
    font-weight: 850;
    padding: 20px;
  }

  .owner-denied {
    width: min(100%, 520px);
    margin: 12vh auto 0;
    text-align: center;
    border: 1px solid #e2e8f0;
    border-radius: 32px;
    background: #fff;
    padding: 34px;
    box-shadow: 0 24px 70px rgba(15,23,42,.08);
  }

  .owner-denied h1 {
    margin: 0 0 10px;
    font-size: 34px;
    letter-spacing: -.055em;
    font-weight: 950;
  }

  .owner-denied p {
    color: #64748b;
    font-weight: 750;
  }

  .owner-denied span {
    display: inline-flex;
    margin-top: 8px;
    color: #475569;
    font-size: 12px;
    font-weight: 850;
  }


  .owner-business-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) minmax(0, 1.4fr) auto;
    gap: 12px;
    align-items: end;
    margin: 0 0 16px;
    border: 1px solid #e2e8f0;
    border-radius: 24px;
    background: rgba(248,250,252,.72);
    padding: 12px;
  }

  .owner-business-search {
    display: grid;
    gap: 7px;
  }

  .owner-business-search span {
    color: #64748b;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: .11em;
    text-transform: uppercase;
  }

  .owner-business-search input {
    width: 100%;
    min-height: 42px;
    border-radius: 15px;
    border: 1px solid #dbe5ee;
    background: #ffffff;
    padding: 0 13px;
    color: #0f172a;
    font-weight: 800;
    outline: none;
  }

  .owner-business-search input:focus {
    border-color: rgba(0,103,95,.32);
    box-shadow: 0 0 0 4px rgba(0,103,95,.08);
  }

  .owner-business-filter-row {
    display: flex;
    align-items: center;
    gap: 7px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 1px;
  }

  .owner-business-filter-row button,
  .owner-export-btn {
    min-height: 42px;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #475569;
    padding: 0 12px;
    font-size: 12px;
    font-weight: 950;
    white-space: nowrap;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  .owner-business-filter-row button.active {
    border-color: transparent;
    background: #0f172a;
    color: #fff;
  }

  .owner-business-filter-row button strong {
    min-width: 20px;
    height: 20px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(0,103,95,.10);
    color: #00675f;
    font-size: 10px;
  }

  .owner-business-filter-row button.active strong {
    background: rgba(255,255,255,.16);
    color: #fff;
  }

  .owner-export-btn {
    justify-content: center;
    background: #00675f;
    color: #fff;
    border-color: transparent;
  }

  .owner-export-btn:disabled {
    opacity: .45;
    cursor: not-allowed;
  }

  .owner-business-list-expanded {
    gap: 12px;
  }

  .owner-business-card.expanded {
    grid-template-columns: minmax(0, 1.15fr) minmax(170px, .42fr) minmax(320px, .72fr);
    align-items: center;
  }

  .owner-business-card.expanded.is-hidden {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    opacity: .82;
  }

  .owner-featured-dot {
    margin-left: 8px;
    display: inline-flex;
    transform: translateY(-1px);
    border-radius: 999px;
    background: #ecfdf5;
    color: #047857;
    padding: 4px 8px;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: 0;
    vertical-align: middle;
  }

  .owner-business-actions.expanded {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .owner-business-actions .neutral,
  .owner-business-actions a.neutral {
    min-height: 38px;
    border-radius: 999px;
    border: 0;
    background: #f1f5f9;
    color: #334155;
    padding: 0 13px;
    font-weight: 950;
    font-size: 13px;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .owner-business-actions a.link {
    background: #ffffff;
    border: 1px solid #e2e8f0;
  }



  /* Phase 1J — Analytics UX Cleanup */
  .owner-analytics-panel-clean {
    background:
      radial-gradient(circle at 0% 0%, rgba(0,103,95,.055), transparent 32%),
      rgba(255,255,255,.92);
  }

  .owner-analytics-head-clean {
    margin-bottom: 14px;
  }

  .owner-analytics-head-clean h2 {
    font-size: clamp(32px, 4vw, 48px);
  }

  .owner-analytics-head-clean p {
    max-width: 560px;
  }

  .owner-analytics-toolbar-clean {
    grid-template-columns: minmax(150px, .44fr) minmax(260px, 1fr) minmax(150px, .42fr) auto;
    border-radius: 22px;
    background: #ffffff;
    box-shadow: 0 12px 34px rgba(15,23,42,.045);
  }

  .owner-data-status-strip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin: 0 0 14px;
    border: 1px solid rgba(0,103,95,.13);
    border-radius: 20px;
    background: linear-gradient(180deg, #ffffff 0%, #f8fffd 100%);
    padding: 12px 14px;
  }

  .owner-data-status-strip strong {
    display: block;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
    letter-spacing: -.02em;
  }

  .owner-data-status-strip span {
    display: block;
    margin-top: 3px;
    color: #64748b;
    font-size: 12px;
    font-weight: 780;
  }

  .owner-data-status-strip button {
    min-height: 36px;
    border: 1px solid rgba(0,103,95,.16);
    border-radius: 999px;
    background: #ffffff;
    color: #00675f;
    padding: 0 13px;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
  }

  .owner-data-health-panel-collapsed {
    margin-top: 0;
  }

  .owner-data-health-grid-compact {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .owner-kpi-row-clean .owner-metric-card {
    min-height: 104px;
    padding: 16px;
  }

  .owner-kpi-row-clean .owner-metric-card strong {
    font-size: 32px;
  }

  .owner-source-summary-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin: 0 0 16px;
  }

  .owner-source-summary-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    border: 1px solid #e2e8f0;
    border-radius: 22px;
    background: #fff;
    padding: 15px 16px;
    box-shadow: 0 12px 34px rgba(15,23,42,.04);
  }

  .owner-source-summary-card span {
    display: block;
    color: #64748b;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: .11em;
    text-transform: uppercase;
  }

  .owner-source-summary-card strong {
    display: block;
    margin-top: 5px;
    color: #0f172a;
    font-size: 30px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: -.055em;
  }

  .owner-source-summary-card small {
    color: #64748b;
    font-size: 12px;
    font-weight: 850;
    text-align: right;
  }

  .owner-source-summary-card.is-online {
    border-color: rgba(0,103,95,.13);
    background: linear-gradient(180deg, #fff 0%, #f0fdfa 100%);
  }

  .owner-source-summary-card.is-manual {
    border-color: rgba(15,23,42,.08);
    background: linear-gradient(180deg, #fff 0%, #f8fafc 100%);
  }

  .owner-source-summary-card.is-unknown {
    border-color: rgba(217,119,6,.16);
    background: linear-gradient(180deg, #fff 0%, #fffbeb 100%);
  }

  .owner-analytics-grid-clean {
    grid-template-columns: minmax(0, 1.18fr) minmax(320px, .82fr);
    gap: 14px;
  }

  .owner-analytics-grid-clean .owner-chart-card {
    box-shadow: 0 12px 34px rgba(15,23,42,.038);
  }

  .owner-bars-clean {
    height: 165px;
  }

  .owner-status-breakdown-clean > div {
    min-height: 44px;
  }

  .owner-ranking-clean > div {
    min-height: 40px;
  }

  .owner-web-activity-card {
    grid-column: 1 / -1;
  }

  .owner-web-activity-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }

  .owner-web-activity-grid > div {
    border: 1px solid #eef2f7;
    border-radius: 16px;
    padding: 10px;
    background: #f8fafc;
  }

  .owner-web-activity-grid span {
    display: block;
    color: #64748b;
    font-size: 11px;
    font-weight: 900;
  }

  .owner-web-activity-grid strong {
    display: block;
    margin-top: 5px;
    color: #0f172a;
    font-size: 24px;
    font-weight: 950;
  }

  .owner-latest-bookings-clean {
    margin-top: 16px;
  }

  .owner-booking-table-clean > div {
    min-height: 44px;
  }

  @media (max-width: 860px) {
    .owner-source-summary-grid,
    .owner-web-activity-grid,
    .owner-data-health-grid-compact {
      grid-template-columns: 1fr;
    }

    .owner-data-status-strip,
    .owner-source-summary-card {
      align-items: stretch;
      flex-direction: column;
    }

    .owner-source-summary-card small {
      text-align: left;
    }

    .owner-analytics-toolbar,
    .owner-analytics-metric-row,
    .owner-source-metric-row,
    .owner-funnel-row {
      grid-template-columns: 1fr;
    }

    .owner-analytics-grid-v2 {
      grid-template-columns: 1fr;
    }

    .owner-booking-table > div {
      grid-template-columns: 1fr;
      gap: 4px;
    }

    .owner-booking-table b {
      text-align: left;
    }

    .owner-data-health-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .owner-data-health-head {
      align-items: stretch;
      flex-direction: column;
    }

    .owner-data-health-actions {
      justify-content: stretch;
    }

    .owner-data-health-actions button {
      width: 100%;
    }

    .owner-console-page {
      padding: 18px 12px calc(28px + env(safe-area-inset-bottom, 0px));
    }

    .owner-console-hero {
      display: block;
    }

    .owner-console-actions {
      margin-top: 16px;
    }

    .owner-console-actions button {
      width: 100%;
    }

    .owner-metrics-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .owner-tabs {
      border-radius: 22px;
    }

    .owner-panel,
    .owner-embedded-early {
      border-radius: 26px;
      padding: 14px;
    }

    .owner-panel-head {
      display: block;
    }

    .owner-panel-head > span {
      margin-top: 10px;
      display: inline-flex;
    }

    .owner-business-card {
      grid-template-columns: 1fr;
      align-items: stretch;
    }

    .owner-business-meta {
      justify-items: start;
      display: flex;
      flex-wrap: wrap;
    }

    .owner-business-actions button {
      flex: 1 1 auto;
    }

    .owner-mini-table > div {
      grid-template-columns: 1fr;
      gap: 4px;
    }

    .owner-analytics-grid {
      grid-template-columns: 1fr;
    }
  }


    .owner-business-toolbar {
      grid-template-columns: 1fr;
      border-radius: 22px;
    }

    .owner-business-card.expanded {
      grid-template-columns: 1fr;
    }

    .owner-business-actions.expanded {
      justify-content: stretch;
    }

    .owner-business-actions .neutral,
    .owner-business-actions a.neutral {
      flex: 1 1 auto;
    }


  @media (max-width: 460px) {
    .owner-metrics-grid {
      grid-template-columns: 1fr;
    }

    .owner-console-hero h1 {
      font-size: 42px;
    }

    .owner-business-actions {
      flex-direction: column;
    }
  }

  .owner-insights-panel {
    border: 1px solid rgba(0,103,95,.12);
    border-radius: 28px;
    background: radial-gradient(circle at 0% 0%, rgba(0,137,127,.10), transparent 34%), linear-gradient(180deg, #ffffff 0%, #f8fffd 100%);
    padding: 18px;
    margin: 14px 0 16px;
    box-shadow: 0 18px 44px rgba(15,23,42,.055);
  }

  .owner-insights-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; margin-bottom: 12px; }
  .owner-insights-head span { color: ${BRAND}; font-size: 11px; font-weight: 950; letter-spacing: .12em; text-transform: uppercase; }
  .owner-insights-head h3 { margin: 4px 0 0; color: #0f172a; font-size: 22px; line-height: 1; letter-spacing: -.045em; font-weight: 950; }
  .owner-insights-head small { border-radius: 999px; background: rgba(0,103,95,.08); color: ${BRAND}; padding: 7px 10px; font-size: 12px; font-weight: 950; white-space: nowrap; }
  .owner-insights-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
  .owner-insight-card { min-height: 96px; border: 1px solid rgba(226,232,240,.95); border-radius: 22px; background: rgba(255,255,255,.88); padding: 14px; display: grid; align-content: start; gap: 6px; }
  .owner-insight-card strong { color: #0f172a; font-size: 13px; font-weight: 950; }
  .owner-insight-card p { margin: 0; color: #64748b; font-size: 12px; line-height: 1.35; font-weight: 780; }
  .owner-insight-card.tone-brand, .owner-insight-card.tone-positive { background: #ecfdf5; border-color: rgba(0,103,95,.14); }
  .owner-insight-card.tone-manual { background: #f8fafc; }
  .owner-insight-card.tone-online { background: #eff6ff; }
  .owner-insight-card.tone-warning { background: #fffbeb; border-color: rgba(217,119,6,.16); }

  .owner-business-performance-list { display: grid; gap: 9px; }
  .owner-business-performance-list > div { display: grid; grid-template-columns: 28px minmax(0, 1fr) auto auto; gap: 10px; align-items: center; border: 1px solid #eef2f7; border-radius: 17px; background: #fff; padding: 10px; }
  .owner-business-performance-list > div > span { width: 24px; height: 24px; border-radius: 999px; background: #ecfdf5; color: ${BRAND}; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 950; }
  .owner-business-performance-list strong { display: block; color: #0f172a; font-size: 13px; font-weight: 950; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .owner-business-performance-list small { display: block; margin-top: 3px; color: #64748b; font-size: 11px; font-weight: 800; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .owner-business-performance-list em, .owner-business-performance-list b { font-style: normal; color: #0f172a; font-size: 12px; font-weight: 950; white-space: nowrap; }
  .owner-business-performance-list b { color: ${BRAND}; }

  .owner-opportunities-panel { border: 1px solid rgba(226,232,240,.95); border-radius: 28px; background: rgba(255,255,255,.90); box-shadow: 0 18px 44px rgba(15,23,42,.055); padding: 18px; margin-top: 14px; }
  .owner-opportunity-list { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-top: 12px; }
  .owner-opportunity-list article { border: 1px solid #eef2f7; border-radius: 20px; background: #fff; padding: 14px; }
  .owner-opportunity-list strong { display: block; color: #0f172a; font-size: 13px; font-weight: 950; }
  .owner-opportunity-list p { margin: 7px 0 10px; color: #64748b; font-size: 12px; line-height: 1.35; font-weight: 780; }
  .owner-opportunity-list span { display: inline-flex; width: fit-content; border-radius: 999px; background: #f1f5f9; color: #334155; padding: 6px 8px; font-size: 11px; font-weight: 950; }

  @media (max-width: 980px) {
    .owner-insights-grid, .owner-opportunity-list { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .owner-business-performance-list > div { grid-template-columns: 28px minmax(0, 1fr) auto; }
    .owner-business-performance-list b { grid-column: 2 / -1; }
  }

  @media (max-width: 640px) {
    .owner-insights-grid, .owner-opportunity-list { grid-template-columns: 1fr; }
    .owner-insights-head { flex-direction: column; }
    .owner-business-performance-list > div { grid-template-columns: 24px minmax(0, 1fr); }
    .owner-business-performance-list em, .owner-business-performance-list b { grid-column: 2 / -1; }
  }


  /* =========================================================
     LOOP OWNER CONSOLE — COMPACT OPERATIONS UI V2
     ========================================================= */
  .owner-console-page {
    min-height: calc(100dvh - 64px) !important;
    padding: 20px clamp(14px, 3vw, 34px) calc(34px + env(safe-area-inset-bottom, 0px)) !important;
    background: #f6f8f8 !important;
  }

  .owner-console-shell {
    width: min(100%, 1320px) !important;
    gap: 14px !important;
  }

  .owner-console-compact-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 18px;
    padding: 2px 2px 0;
  }

  .owner-console-compact-title { min-width: 0; }
  .owner-console-compact-head .owner-console-kicker { margin-bottom: 6px; font-size: 10.5px; letter-spacing: .15em; }
  .owner-console-title-line { display: flex; align-items: baseline; gap: 14px; flex-wrap: wrap; }
  .owner-console-title-line h1 { margin: 0; color: #0f172a; font-size: clamp(30px, 3.2vw, 44px); line-height: 1; letter-spacing: -.055em; font-weight: 880; }
  .owner-console-title-line > span { color: #64748b; font-size: 13px; font-weight: 800; }

  .owner-refresh-button {
    min-height: 40px;
    border: 1px solid #d9e2e3;
    border-radius: 12px;
    background: #fff;
    color: #0f172a;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 850;
    cursor: pointer;
    box-shadow: 0 6px 18px rgba(15,23,42,.04);
  }
  .owner-refresh-button:disabled { opacity: .58; cursor: wait; }
  .owner-refresh-button .is-spinning { animation: ownerSpin .85s linear infinite; }
  @keyframes ownerSpin { to { transform: rotate(360deg); } }

  .owner-summary-strip {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border: 1px solid #dfe7e8;
    border-radius: 16px;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(15,23,42,.035);
  }
  .owner-summary-strip > div { min-height: 62px; padding: 11px 16px; display: flex; align-items: center; justify-content: space-between; gap: 10px; border-right: 1px solid #edf1f2; }
  .owner-summary-strip > div:last-child { border-right: 0; }
  .owner-summary-strip span { color: #64748b; font-size: 12px; font-weight: 800; }
  .owner-summary-strip strong { color: #0f172a; font-size: 22px; line-height: 1; font-weight: 900; }
  .owner-summary-strip .is-pending strong { color: #b45309; }
  .owner-summary-strip .is-active strong { color: ${BRAND}; }

  .owner-tabs.owner-tabs-compact {
    width: fit-content;
    max-width: 100%;
    min-height: 42px;
    padding: 4px;
    gap: 3px;
    border-radius: 13px;
    border: 1px solid #dfe7e8;
    background: #fff;
    overflow-x: auto;
    box-shadow: none;
  }
  .owner-tabs.owner-tabs-compact button { min-height: 32px; padding: 0 13px; border-radius: 9px; font-size: 12.5px; }
  .owner-tabs.owner-tabs-compact button.active { background: #0f172a; color: #fff; box-shadow: none; }

  .owner-panel.owner-businesses-panel-compact,
  .owner-panel.owner-settings-compact {
    padding: 18px !important;
    border-radius: 18px !important;
    border: 1px solid #dfe7e8 !important;
    background: #fff !important;
    box-shadow: 0 10px 28px rgba(15,23,42,.04) !important;
  }

  .owner-businesses-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
  .owner-businesses-heading h2 { margin: 0; color: #0f172a; font-size: 22px; line-height: 1.05; letter-spacing: -.035em; font-weight: 900; }
  .owner-businesses-heading p { margin: 5px 0 0; color: #64748b; font-size: 12.5px; line-height: 1.4; font-weight: 700; }
  .owner-businesses-heading > span { border-radius: 999px; background: #f1f5f5; color: #475569; padding: 6px 9px; font-size: 11px; font-weight: 850; white-space: nowrap; }

  .owner-business-toolbar-compact { display: grid; grid-template-columns: minmax(220px, 1fr) auto auto; align-items: center; gap: 10px; margin-bottom: 14px; }
  .owner-business-search-compact { min-height: 40px; display: flex; align-items: center; gap: 9px; border: 1px solid #dfe7e8; border-radius: 11px; background: #fafcfc; padding: 0 11px; color: #64748b; }
  .owner-business-search-compact:focus-within { border-color: rgba(0,103,95,.35); box-shadow: 0 0 0 3px rgba(0,103,95,.08); background: #fff; }
  .owner-business-search-compact input { min-width: 0; width: 100%; border: 0; outline: 0; background: transparent; color: #0f172a; font-size: 13px; font-weight: 700; }
  .owner-business-search-compact button { width: 28px; height: 28px; border: 0; border-radius: 8px; background: transparent; color: #64748b; display: grid; place-items: center; cursor: pointer; }

  .owner-filter-pills-compact { display: flex; align-items: center; gap: 5px; overflow-x: auto; scrollbar-width: none; }
  .owner-filter-pills-compact::-webkit-scrollbar { display: none; }
  .owner-filter-pills-compact button { min-height: 34px; border: 1px solid #e2e8e9; border-radius: 9px; background: #fff; color: #475569; padding: 0 9px; display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 820; white-space: nowrap; cursor: pointer; }
  .owner-filter-pills-compact button strong { min-width: 18px; height: 18px; border-radius: 999px; background: #f1f5f5; color: #64748b; display: inline-flex; align-items: center; justify-content: center; font-size: 10px; }
  .owner-filter-pills-compact button.active { border-color: rgba(0,103,95,.22); background: #eaf7f5; color: ${BRAND}; }
  .owner-filter-pills-compact button.active strong { background: ${BRAND}; color: #fff; }
  .owner-export-compact { min-height: 34px; border: 1px solid #dfe7e8; border-radius: 9px; background: #fff; color: #475569; padding: 0 10px; font-size: 11px; font-weight: 900; cursor: pointer; }

  .owner-business-rows { display: grid; gap: 7px; }
  .owner-business-row {
    position: relative;
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    min-height: 72px;
    padding: 10px 11px;
    border: 1px solid #e5ebec;
    border-radius: 13px;
    background: #fff;
    transition: border-color 140ms ease, box-shadow 140ms ease, background 140ms ease;
  }
  .owner-business-row:hover { border-color: #cfdadb; box-shadow: 0 7px 20px rgba(15,23,42,.045); }
  .owner-business-row.is-hidden { background: #fafafa; opacity: .78; }

  .owner-business-row-avatar { position: relative; width: 42px; height: 42px; border-radius: 11px; overflow: hidden; background: #e7f4f2; color: ${BRAND}; display: grid; place-items: center; font-size: 16px; font-weight: 900; }
  .owner-business-row-avatar img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; z-index: 1; }
  .owner-business-row-avatar span { position: relative; z-index: 0; }
  .owner-business-row-info { min-width: 0; }
  .owner-business-row-title { display: flex; align-items: center; gap: 7px; min-width: 0; flex-wrap: wrap; }
  .owner-business-row-title h3 { min-width: 0; max-width: 100%; margin: 0; color: #0f172a; font-size: 14px; line-height: 1.15; letter-spacing: -.018em; font-weight: 900; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .owner-business-row-info p { margin: 4px 0 0; color: #52606d; font-size: 11.5px; line-height: 1.25; font-weight: 740; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .owner-business-row-info small { display: block; margin-top: 3px; color: #94a3b8; font-size: 10.5px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .owner-business-row .owner-status { padding: 4px 7px; font-size: 9.5px; letter-spacing: .02em; }
  .owner-featured-chip, .owner-hidden-chip { border-radius: 999px; padding: 4px 7px; font-size: 9.5px; font-weight: 850; }
  .owner-featured-chip { background: #fffbeb; color: #a16207; }
  .owner-hidden-chip { background: #f1f5f9; color: #64748b; }

  .owner-business-row-actions { display: flex; align-items: center; justify-content: flex-end; gap: 7px; }
  .owner-row-action { min-height: 34px; border-radius: 9px; padding: 0 11px; display: inline-flex; align-items: center; justify-content: center; gap: 6px; font-size: 11.5px; font-weight: 900; cursor: pointer; }
  .owner-row-action.approve { border: 1px solid rgba(0,103,95,.20); background: #eaf7f5; color: ${BRAND}; }
  .owner-row-action.reject { border: 1px solid #fecaca; background: #fff; color: #b42318; }
  .owner-row-action:disabled { opacity: .55; cursor: wait; }

  .owner-business-menu-wrap { position: relative; }
  .owner-more-button { width: 34px; height: 34px; border: 1px solid #dfe7e8; border-radius: 9px; background: #fff; color: #475569; display: grid; place-items: center; cursor: pointer; }
  .owner-more-button:hover, .owner-more-button[aria-expanded="true"] { background: #f1f5f5; color: #0f172a; }
  .owner-business-menu { position: absolute; z-index: 40; top: calc(100% + 6px); right: 0; width: 208px; padding: 6px; border: 1px solid #dfe7e8; border-radius: 12px; background: #fff; box-shadow: 0 18px 44px rgba(15,23,42,.16); }
  .owner-business-menu button, .owner-business-menu a { width: 100%; min-height: 34px; border: 0; border-radius: 8px; background: transparent; color: #334155; padding: 0 9px; display: flex; align-items: center; gap: 9px; text-align: left; text-decoration: none; font-size: 11.5px; font-weight: 800; cursor: pointer; }
  .owner-business-menu button:hover, .owner-business-menu a:hover { background: #f1f5f5; color: #0f172a; }
  .owner-business-menu .danger { color: #b42318; }

  .owner-business-empty-compact { min-height: 160px; display: grid; place-items: center; align-content: center; gap: 7px; border: 1px dashed #cfdadb; border-radius: 14px; background: #fafcfc; color: #64748b; text-align: center; }
  .owner-business-empty-compact strong { color: #0f172a; font-size: 13px; }
  .owner-business-empty-compact span { font-size: 11.5px; font-weight: 700; }
  .owner-loading-compact { min-height: 120px; border-radius: 14px; }

  .owner-settings-list { display: grid; gap: 7px; }
  .owner-settings-list > div { min-height: 48px; border: 1px solid #e5ebec; border-radius: 11px; padding: 9px 12px; display: flex; align-items: center; justify-content: space-between; gap: 14px; }
  .owner-settings-list span { color: #64748b; font-size: 11.5px; font-weight: 750; }
  .owner-settings-list strong { color: #0f172a; font-size: 12px; font-weight: 880; text-align: right; }

  .owner-modal-backdrop { position: fixed; inset: 0; z-index: 10000; background: rgba(15,23,42,.46); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); display: grid; place-items: center; padding: 18px; }
  .owner-reject-modal { position: relative; width: min(100%, 470px); border-radius: 18px; background: #fff; border: 1px solid rgba(255,255,255,.75); box-shadow: 0 30px 90px rgba(15,23,42,.28); padding: 22px; }
  .owner-modal-close { position: absolute; top: 12px; right: 12px; width: 34px; height: 34px; border: 1px solid #e2e8f0; border-radius: 9px; background: #fff; color: #64748b; display: grid; place-items: center; cursor: pointer; }
  .owner-modal-kicker { color: #b42318; font-size: 10.5px; font-weight: 900; letter-spacing: .12em; text-transform: uppercase; }
  .owner-reject-modal h2 { margin: 7px 38px 0 0; color: #0f172a; font-size: 24px; line-height: 1.06; letter-spacing: -.04em; font-weight: 900; }
  .owner-reject-modal > p { margin: 9px 0 16px; color: #64748b; font-size: 12.5px; line-height: 1.45; font-weight: 700; }
  .owner-reject-modal label > span { display: block; margin-bottom: 7px; color: #334155; font-size: 11.5px; font-weight: 850; }
  .owner-reject-modal textarea { width: 100%; resize: vertical; min-height: 104px; border: 1px solid #dfe7e8; border-radius: 11px; background: #fafcfc; color: #0f172a; padding: 11px; outline: none; font: inherit; font-size: 13px; line-height: 1.45; }
  .owner-reject-modal textarea:focus { border-color: rgba(180,35,24,.35); box-shadow: 0 0 0 3px rgba(180,35,24,.08); background: #fff; }
  .owner-reject-modal label small { display: block; margin-top: 5px; color: #94a3b8; font-size: 10px; text-align: right; }
  .owner-modal-error { margin-top: 9px; border-radius: 9px; background: #fef2f2; color: #b42318; padding: 8px 10px; font-size: 11.5px; font-weight: 800; }
  .owner-modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
  .owner-modal-actions button { min-height: 38px; border-radius: 10px; padding: 0 13px; font-size: 12px; font-weight: 900; cursor: pointer; }
  .owner-modal-actions .secondary { border: 1px solid #dfe7e8; background: #fff; color: #475569; }
  .owner-modal-actions .danger { border: 0; background: #b42318; color: #fff; }
  .owner-modal-actions button:disabled { opacity: .58; cursor: wait; }

  @media (max-width: 960px) {
    .owner-business-toolbar-compact { grid-template-columns: 1fr auto; }
    .owner-filter-pills-compact { grid-column: 1 / -1; grid-row: 2; }
    .owner-business-row { grid-template-columns: 42px minmax(0, 1fr); }
    .owner-business-row-actions { grid-column: 1 / -1; justify-content: flex-end; padding-top: 2px; }
  }

  @media (max-width: 640px) {
    .owner-console-page { padding: 14px 10px calc(24px + env(safe-area-inset-bottom, 0px)) !important; }
    .owner-console-compact-head { align-items: flex-start; }
    .owner-console-title-line { display: block; }
    .owner-console-title-line h1 { font-size: 31px; }
    .owner-console-title-line > span { display: block; margin-top: 5px; }
    .owner-refresh-button { width: 40px; padding: 0; }
    .owner-refresh-button:not(:disabled) { font-size: 0; gap: 0; }
    .owner-refresh-button svg { width: 17px; height: 17px; }
    .owner-summary-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .owner-summary-strip > div { min-height: 54px; border-bottom: 1px solid #edf1f2; }
    .owner-summary-strip > div:nth-child(2) { border-right: 0; }
    .owner-summary-strip > div:nth-child(n+3) { border-bottom: 0; }
    .owner-panel.owner-businesses-panel-compact, .owner-panel.owner-settings-compact { padding: 13px !important; }
    .owner-businesses-heading > span { display: none; }
    .owner-business-toolbar-compact { grid-template-columns: 1fr auto; }
    .owner-business-row { padding: 10px; }
    .owner-business-row-info p, .owner-business-row-info small { white-space: normal; }
    .owner-business-row-actions { justify-content: stretch; }
    .owner-row-action { flex: 1 1 auto; }
    .owner-business-menu-wrap { flex: 0 0 auto; }
    .owner-business-menu { position: fixed; left: 12px; right: 12px; bottom: calc(12px + env(safe-area-inset-bottom, 0px)); top: auto; width: auto; border-radius: 16px; padding: 8px; }
    .owner-business-menu button, .owner-business-menu a { min-height: 44px; font-size: 13px; }
    .owner-settings-list > div { align-items: flex-start; flex-direction: column; gap: 4px; }
    .owner-settings-list strong { text-align: left; word-break: break-word; }
  }

`;

